// src/utils/exportUtils.js
import * as XLSX from 'xlsx';
import { strToU8, strFromU8, unzipSync, zipSync } from 'fflate';
import moment from 'moment';
import { exportDates } from '../components/TwoWeekDateCalculator';
import { getDepositData, calculateCdpDepositFields } from './cdpDepositUtils';
import { getAltPayFlag } from './doctorPayHelpers';
import { buildNdpValidationData } from './ndpValidation';


const getDoctorNameWithPrefix = (name) => {
    if (!name) return '';
    name = name.toString().trim();
    // Remove all existing "Dr." or "Dr " prefixes
    name = name.replace(/^(dr\.?\s*)+/i, '');
    // Add a single "Dr. " prefix
    return `Dr. ${name}`;
};

/**
 * Filters the raw pay data to exclude temporary or zero-pay providers,
 * mirroring the logic used for the main CDP report generation.
 * This ensures consistency between the master report and individual exports.
 *
 * @param {Array} rawPayData - The groupByDoctorByPractice array from the pay data API response.
 * @returns {Array} The filtered pay data.
 */
export const filterPayDataForExport = (rawPayData) => {
    if (!Array.isArray(rawPayData)) {
        return [];
    }
    // This filter is the key to matching the master report's output.
    return rawPayData.filter(item => {
        return item && item.procBasedPay !== 0 && item.contractType !== 'TMPDOC' && item.contractType !== 'TMPHYN';
    });
};

/**
 * Merge missing provider records from a secondary source (doctorDetails/doctorList) into
 * an exports input array when a provider exists in the UI lists but not in the
 * billing `groupByDoctorByPractice` array. This is a safe, minimal merge that will
 * copy over identifying metadata so export utilities can emit rows where possible.
 *
 * @param {Array} primary - Array used for exports (e.g. groupByDoctorByPractice)
 * @param {Object|Array} secondary - doctorDetails (object keyed by abbr) or doctorList (array)
 * @returns {Array} merged array with primary entries plus fallback entries from secondary
 */
export const mergeMissingDoctorsForExport = (primary = [], secondary = null) => {
    try {
        const out = Array.isArray(primary) ? [...primary] : [];

        // Build a lookup of existing ABBR values in primary
        const primaryAbbrs = new Set((out || []).map(p => (p && p.ABBR && String(p.ABBR).toUpperCase()) || ''));

        // Normalize secondary into an array of doctor objects
        let secondaryArr = [];
        if (!secondary) secondaryArr = [];
        else if (Array.isArray(secondary)) secondaryArr = secondary;
        else if (typeof secondary === 'object') secondaryArr = Object.values(secondary || {});

        // Build quick lookups for companyCode from the secondary source so we can
        // populate missing `companyCode` values on primary provider entries.
        const secondaryCompanyByAbbr = new Map();
        const secondaryCompanyByUkgid = new Map();
        const secondaryCompanyByAdpId = new Map();
        secondaryArr.forEach(doc => {
            try {
                const abbrKey = (doc && (doc.abbr || doc.ABBR) && String(doc.abbr || doc.ABBR).toUpperCase()) || '';
                const ukgidKey = doc && (doc.ukgid || (doc.someValues && doc.someValues.ukgid)) ? String(doc.ukgid || (doc.someValues && doc.someValues.ukgid)) : '';
                const adpKey = doc && (doc.adpId || (doc.someValues && doc.someValues.adpId)) ? String(doc.adpId || (doc.someValues && doc.someValues.adpId)) : '';
                const company = doc.companyCode || (doc.someValues && doc.someValues.companyCode) || (doc.Doctors && doc.Doctors[0] && doc.Doctors[0].companyCode) || '';
                if (abbrKey) secondaryCompanyByAbbr.set(abbrKey, company);
                if (ukgidKey) secondaryCompanyByUkgid.set(ukgidKey, company);
                if (adpKey) secondaryCompanyByAdpId.set(adpKey, company);
            } catch (e) {
                // ignore malformed doc entries
            }
        });

        secondaryArr.forEach((doc) => {
            try {
                const abbr = (doc && (doc.abbr || doc.ABBR) && String(doc.abbr || doc.ABBR).toUpperCase()) || '';
                if (!abbr) return;
                if (primaryAbbrs.has(abbr)) return; // already present

                // Only merge providers that are not altPay (or explicitly false)
                const alt = (doc && (doc.altPay || (doc.someValues && doc.someValues.altPay) || (doc.Doctors && doc.Doctors[0] && doc.Doctors[0].altPay)));
                if (alt === true) return;

                // Heuristic: only include fallback when the doctor has meaningful numeric data
                // (care, bCare, proc/Total Pay, financial notes, or deposits)
                let hasMeaningfulData = false;

                // Check top-level numeric fields
                const topProc = Number(doc.procBasedPay || doc.procPay || doc.totalPay || doc.pay || 0);
                if (topProc > 0) hasMeaningfulData = true;

                const topFinNotes = Number(doc.financialNotes || doc.finNotes || 0);
                if (topFinNotes > 0) hasMeaningfulData = true;

                if ((doc.financialNoteList && Array.isArray(doc.financialNoteList) && doc.financialNoteList.length > 0)) {
                    // if list contains numeric amounts, sum them
                    const sum = doc.financialNoteList.reduce((s, it) => s + (Number(it.amount || it.value || 0) || 0), 0);
                    if (sum > 0) hasMeaningfulData = true;
                }

                if ((doc.someValues && doc.someValues.financialNoteList && Array.isArray(doc.someValues.financialNoteList) && doc.someValues.financialNoteList.length > 0)) {
                    const sum = doc.someValues.financialNoteList.reduce((s, it) => s + (Number(it.amount || it.value || 0) || 0), 0);
                    if (sum > 0) hasMeaningfulData = true;
                }

                // Check deposits
                if (Number(doc.firstDeposit || 0) > 0 || Number(doc.secondDeposit || 0) > 0) hasMeaningfulData = true;

                // Inspect per-practice data if available
                if (doc.data && typeof doc.data === 'object') {
                    Object.values(doc.data).forEach(prac => {
                        if (!prac || typeof prac !== 'object') return;
                        const proc = Number(prac.procBasedPay || prac.totalPay || prac.procPay || 0);
                        const pBcare = Number(prac.practiceBcare || prac.bCare || prac.billableCare || prac.practiceBillableCare || 0);
                        const pFin = Number(prac.financialNotes || prac.financialNote || 0);
                        if (proc > 0 || pBcare > 0 || pFin > 0) hasMeaningfulData = true;
                    });
                }

                // If no meaningful data, skip merging this doctor
                if (!hasMeaningfulData) return;

                // Require that the doctor has at least one practice visible to CDP Reports
                // (either doctorPracticesArray or a populated `data` object with practice entries)
                let hasPractice = false;
                if (Array.isArray(doc.doctorPracticesArray) && doc.doctorPracticesArray.length > 0) {
                    hasPractice = true;
                } else if (doc.data && typeof doc.data === 'object') {
                    const pracKeys = Object.keys(doc.data).filter(k => k !== 'reportData');
                    if (pracKeys.length > 0) {
                        for (const pk of pracKeys) {
                            const p = doc.data[pk];
                            if (!p) continue;
                            // consider practice present if it contains practiceInfo, price, patientVisits, financialNotes, or procBasedPay
                            if (p.practiceInfo || p.price || p.patientVisits || p.financialNotes || p.procBasedPay) {
                                hasPractice = true;
                                break;
                            }
                        }
                    }
                }

                if (!hasPractice) return;

                // Minimal provider object to allow exports to include provider-level rows.
                // Keep fields that export utilities expect (ABBR, doctorName, ukgid, role, payType)
                const fallback = {
                    ABBR: abbr,
                    doctorName: (doc && ((doc.firstName && doc.lastName) ? `Dr. ${doc.firstName} ${doc.lastName}` : (doc.doctorName || (doc.someValues && `${doc.someValues.firstName || ''} ${doc.someValues.lastName || ''}`)))) || abbr,
                    ukgid: doc.ukgid || (doc.someValues && doc.someValues.ukgid) || '',
                    adpId: doc.adpId || (doc.someValues && doc.someValues.adpId) || '',
                    companyCode: doc.companyCode || (doc.someValues && doc.someValues.companyCode) || (doc.Doctors && doc.Doctors[0] && doc.Doctors[0].companyCode) || '',
                    role: doc.role || (doc.someValues && doc.someValues.role) || 'Dentist',
                    payType: doc.payType || (doc.someValues && doc.someValues.payType) || 'W2',
                    contractType: doc.contractName || (doc.someValues && doc.someValues.contractName) || 'Unknown',
                    procBasedPay: topProc || 0,
                };

                // Add the fallback provider into the output array so exports will include the doctor
                out.push(fallback);
            }
            catch (e) {
                // ignore malformed doctor entries
            }
        });

        // After adding fallback entries, backfill missing `companyCode` on primary
        // and merged provider objects using secondary lookups when possible.
        out.forEach(p => {
            try {
                if (!p.companyCode || String(p.companyCode || '').trim() === '') {
                    const abbr = (p && (p.ABBR || p.abbr) && String(p.ABBR || p.abbr).toUpperCase()) || '';
                    const ukgid = p && (p.ukgid || '') ? String(p.ukgid) : '';
                    const adpId = p && (p.adpId || '') ? String(p.adpId) : '';
                    const fromAbbr = secondaryCompanyByAbbr.get(abbr);
                    const fromUkg = secondaryCompanyByUkgid.get(ukgid);
                    const fromAdp = secondaryCompanyByAdpId.get(adpId);
                    const companyFromSecondary = fromAbbr || fromUkg || fromAdp;
                    if (companyFromSecondary) {
                        p.companyCode = companyFromSecondary;
                    }
                }
            } catch (e) { }
        });

        // Finished processing secondary doctors — return merged list
        return out;
    }
    catch (e) {
        // On unexpected errors, return original primary array
        return Array.isArray(primary) ? primary : [];
    }

    // End of mergeMissingDoctorsForExport
};

// Module-level sanitize helpers for XLSX exports
export function sanitizeCell(value) {
    if (value === undefined || value === null || Number.isNaN(value)) return "";
    if (value instanceof Date) return value;
    if (typeof value === "object") return JSON.stringify(value);
    if (typeof value === "string") {
        return value.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F]/g, "");
    }
    return value;
}

export function sanitizeData(data) {
    if (!Array.isArray(data)) {
        throw new Error("sanitizeData: expected an array but got " + typeof data);
    }

    // Array of objects → JSON-to-sheet
    if (data.length && typeof data[0] === "object" && !Array.isArray(data[0])) {
        return data.map(row => {
            const newRow = {};
            for (const key in row) {
                if (Object.prototype.hasOwnProperty.call(row, key)) {
                    newRow[key] = sanitizeCell(row[key]);
                }
            }
            return newRow;
        });
    }

    // Array of arrays → AOA-to-sheet
    return data.map(row => {
        if (!Array.isArray(row)) {
            throw new Error("sanitizeData: row must be array or object");
        }
        return row.map(sanitizeCell);
    });
}

function XLSXaddSheet(workbook, data, sheetName) {
    const safeData = sanitizeData(data);

    let ws;
    if (safeData.length && typeof safeData[0] === "object" && !Array.isArray(safeData[0])) {
        ws = XLSX.utils.json_to_sheet(safeData);
    } else {
        ws = XLSX.utils.aoa_to_sheet(safeData);
    }

    const cleanName = sheetName
        .substring(0, 31)
        .replace(/[\\\/\?\*\[\]\:]/g, "_");

    XLSX.utils.book_append_sheet(workbook, ws, cleanName);
}

/**
 * Post-process an xlsx buffer to apply colored fills to specific cells.
 * Uses fflate (already a dependency) to patch the zip XML directly, since
 * SheetJS community edition does not support writing cell fill styles.
 *
 * @param {ArrayBuffer|Uint8Array|number[]} xlsxBuffer - from XLSX.write({type:'array'})
 * @param {Array<{sheetName:string, cellAddrs:string[], color?:string}>} highlights
 *   - color: optional 'yellow'|'green'|'red'; defaults to 'yellow' for backward compatibility
 * @returns {Uint8Array|ArrayBuffer|number[]} patched xlsx bytes (or original buffer on error)
 */
function applyXlsxHighlights(xlsxBuffer, highlights) {
    if (!highlights || highlights.length === 0) return xlsxBuffer;
    try {
        const uint8 = xlsxBuffer instanceof Uint8Array ? xlsxBuffer : new Uint8Array(xlsxBuffer);
        const files = unzipSync(uint8);

        // Color definitions (Excel good/bad colors for green/red, yellow for legacy)
        const colorDefs = {
            yellow: { fill: 'FFFF00', font: '' },
            green: { fill: 'C6EFCE', font: '006100' },
            red: { fill: 'FFC7CE', font: '9C0006' }
        };

        // Collect unique colors needed
        const colorsUsed = new Set(['yellow']); // always add yellow for backwards compat
        highlights.forEach(h => {
            const color = h.color || 'yellow';
            colorsUsed.add(color);
        });

        // 1. Patch xl/styles.xml — add fills + fonts + cellXfs for each color
        const stylesKey = 'xl/styles.xml';
        if (!files[stylesKey]) return xlsxBuffer;
        let stylesXml = strFromU8(files[stylesKey]);

        const fillCountMatch = stylesXml.match(/<fills count="(\d+)"/);
        let fillCount = fillCountMatch ? parseInt(fillCountMatch[1], 10) : 2;
        const fillIndexMap = new Map(); // color → fill index

        const fontCountMatch = stylesXml.match(/<fonts count="(\d+)"/);
        let fontCount = fontCountMatch ? parseInt(fontCountMatch[1], 10) : 1;
        const fontIndexMap = new Map(); // color → font index

        // Add fills and fonts for each color, track indices
        colorsUsed.forEach(color => {
            const def = colorDefs[color] || colorDefs.yellow;
            fillIndexMap.set(color, fillCount);
            if (def.font) {
                fontIndexMap.set(color, fontCount);
                fontCount++;
            } else {
                fontIndexMap.set(color, 0); // use default font
            }
            fillCount++;
        });

        // Insert fills into styles.xml
        let fillsToAdd = Array.from(colorsUsed)
            .map(color => {
                const def = colorDefs[color];
                return `<fill><patternFill patternType="solid"><fgColor rgb="${def.fill}"/></patternFill></fill>`;
            })
            .join('');
        stylesXml = stylesXml
            .replace(/<fills count="\d+"/, `<fills count="${fillCount}"`)
            .replace('</fills>', `${fillsToAdd}</fills>`);

        // Insert fonts into styles.xml (only if new fonts are needed)
        const newFonts = Array.from(colorsUsed)
            .filter(color => fontIndexMap.get(color) > 0)
            .map(color => {
                const def = colorDefs[color];
                return `<font><color rgb="${def.font}"/></font>`;
            });
        if (newFonts.length > 0) {
            stylesXml = stylesXml
                .replace(/<fonts count="\d+"/, `<fonts count="${fontCount}"`)
                .replace('</fonts>', `${newFonts.join('')}</fonts>`);
        }

        // Insert cellXfs for each color
        const xfsCountMatch = stylesXml.match(/<cellXfs count="(\d+)"/);
        let xfsCount = xfsCountMatch ? parseInt(xfsCountMatch[1], 10) : 1;
        const xfIndexMap = new Map(); // color → xf index
        let xfsToAdd = '';
        colorsUsed.forEach(color => {
            xfIndexMap.set(color, xfsCount);
            const fillIdx = fillIndexMap.get(color);
            const fontIdx = fontIndexMap.get(color);
            xfsToAdd += `<xf numFmtId="0" fontId="${fontIdx}" fillId="${fillIdx}" borderId="0" xfId="0" applyFill="1" applyFont="${fontIdx > 0 ? 1 : 0}"/>`;
            xfsCount++;
        });
        stylesXml = stylesXml
            .replace(/<cellXfs count="\d+"/, `<cellXfs count="${xfsCount}"`)
            .replace('</cellXfs>', `${xfsToAdd}</cellXfs>`);

        files[stylesKey] = strToU8(stylesXml);

        // 2. Build sheetName → zip file path map from workbook.xml + relationships
        const wbXml = files['xl/workbook.xml'] ? strFromU8(files['xl/workbook.xml']) : '';
        const relsXml = files['xl/_rels/workbook.xml.rels'] ? strFromU8(files['xl/_rels/workbook.xml.rels']) : '';
        const rIdToName = new Map();
        let _m;
        const _sheetRx = /<sheet\b[^>]*\bname="([^"]*)"[^>]*\br:id="([^"]*)"/g;
        while ((_m = _sheetRx.exec(wbXml)) !== null) rIdToName.set(_m[2], _m[1]);
        const nameToPath = new Map();
        const _relRx = /<Relationship\b[^>]*\bId="([^"]*)"[^>]*\bTarget="([^"]*)"/g;
        while ((_m = _relRx.exec(relsXml)) !== null) {
            const _name = rIdToName.get(_m[1]);
            if (_name) nameToPath.set(_name, `xl/${_m[2]}`);
        }

        // 3. Patch each targeted worksheet's cell XML
        for (const { sheetName, cellAddrs, color = 'yellow' } of highlights) {
            if (!cellAddrs || cellAddrs.length === 0) continue;
            const wsPath = nameToPath.get(sheetName);
            if (!wsPath || !files[wsPath]) continue;
            const xfIdx = xfIndexMap.get(color) || xfIndexMap.get('yellow');
            let wsXml = strFromU8(files[wsPath]);
            for (const addr of cellAddrs) {
                const esc = addr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                wsXml = wsXml
                    .replace(new RegExp(`(<c\\b(?=[^>]*?\\br="${esc}")[^>]*?)(?:\\s+s="\\d+")`), '$1')
                    .replace(new RegExp(`(<c\\b(?=[^>]*?\\br="${esc}")[^>]*?)(\\s*/)?>`, 'g'),
                        (_, pre, selfClose) => `${pre} s="${xfIdx}"${selfClose || ''}>`);
            }
            files[wsPath] = strToU8(wsXml);
        }

        // 4. Re-zip and return
        return zipSync(files, { level: 6 });
    } catch (e) {
        return xlsxBuffer;
    }
}

/**
 * Convert already-computed deposit rows (same rows written to DEPOSIT sheet) into ADP format.
 * Guarantees ADP amounts are identical to the DEPOSIT sheet because they share the same source.
 *
 * @param {Array}  depositRows - rows already formatted for the DEPOSIT sheet (have 'E/D/T Code', 'E/D/T Amount', 'ADP ID', 'Employee Id')
 * @param {string} payDateStr  - ADP pay date string (MMDDYYYY)
 * @param {Map}    coCodeMap    - Map keyed 'adp:<id>' / 'ukg:<id>' → companyCode
 * @param {Map}    tempCostMap  - Map keyed by adpId → Temp Cost Number string
 */
function depositRowsToAdpRows(depositRows, payDateStr, coCodeMap = new Map(), tempCostMap = new Map(), allCostsMap = new Map()) {
    const earningsCodeMap = { COMM: 'COM', BONUS: 'BON', '1099': '109', ADVANCE: 'ADV' };
    return (depositRows || []).reduce((rows, r) => {
        const code = ((r['E/D/T Code'] || r['E/D/T'] || '') + '').toString().toUpperCase();
        const amount = Number(r['E/D/T Amount'] || 0);
        if (amount === 0) return rows;

        const adpId = r['ADP ID'] || '';
        const ukgId = r['Employee Id'] || '';
        const cc1 = r['CC1'] || '';
        const coCode = coCodeMap.get(`adp:${adpId}`) || coCodeMap.get(`ukg:${ukgId}`) || r['CO CODE'] || '';
        const isReimb = code === 'REIMB';
        const mappedCode = earningsCodeMap[code] || (code ? code.substring(0, 3) : '');
        const providerName = r['Username'] || '';
        // _tempCostBase embedded in row at build time takes precedence (guaranteed per-practice accuracy)
        const embeddedBase = r['_tempCostBase'] || '';
        const fallbackBase = !embeddedBase ? (tempCostMap.get(`${adpId}:${cc1}`) || tempCostMap.get(String(adpId)) || '') : '';
        const _tempCostBase = embeddedBase || fallbackBase;
        const _isFallback = !embeddedBase && !!fallbackBase;
        // When fallback is used, list ALL available temp cost numbers for this provider
        // so the analyst can see which practice the code should come from.
        let tempCostNumber;
        if (_isFallback) {
            const allCosts = allCostsMap.get(String(adpId)) || [];
            tempCostNumber = allCosts.length > 0
                ? allCosts.map(c => `${c}-DC0000`).join(', ')
                : (_tempCostBase ? `${_tempCostBase}-DC0000` : '');
        } else {
            tempCostNumber = _tempCostBase ? `${_tempCostBase}-DC0000` : '';
        }

        if (isReimb) {
            rows.push({
                'Provider': providerName,
                'Co Code': coCode,
                'Batch ID': payDateStr || '',
                'File #': adpId,
                'Earnings 3 Code': '',
                'Earnings 3 Amount': '',
                'Adjust DED Code': 'REI',
                'Adjust DED Amount': (-Number(amount)).toFixed(2),
                'Temp Cost Number': tempCostNumber,
                '_isFallback': _isFallback
            });
        } else {
            rows.push({
                'Provider': providerName,
                'Co Code': coCode,
                'Batch ID': payDateStr || '',
                'File #': adpId,
                'Earnings 3 Code': mappedCode,
                'Earnings 3 Amount': Number(amount).toFixed(2),
                'Adjust DED Code': '',
                'Adjust DED Amount': '',
                'Temp Cost Number': tempCostNumber,
                '_isFallback': _isFallback
            });
        }
        return rows;
    }, []);
}

/**
 * Build FUNDING 1ST/2ND rows from deposit data.
 * Groups by Entity (sage location) + UKG Account Name, summing amounts by E/D/T Code.
 *
 * @param {Array}  depositRows1   - rows formatted for DEPOSIT 1ST
 * @param {Array}  depositRows2   - rows formatted for DEPOSIT 2ND
 * @param {Array}  providerData   - original provider data (for UKG ID, ADP ID, metadata)
 * @param {string} startDate
 * @param {Map}    coCodeMap      - Map keyed 'adp:<id>' / 'ukg:<id>' → companyCode
 * @returns {Array} fundingData1, fundingData2 arrays
 */
function depositRowsToFundingRows(depositRows1, depositRows2, providerData, startDate, coCodeMap = new Map()) {
    const providerByUkg = new Map();
    const providerByAdp = new Map();
    (providerData || []).forEach(item => {
        if (!item) return;
        if (item.ukgid) providerByUkg.set(String(item.ukgid), item);
        if (item.adpId) providerByAdp.set(String(item.adpId), item);
    });
    const getMeta = (ukgId, adpId) =>
        providerByUkg.get(String(ukgId || '')) || providerByAdp.get(String(adpId || '')) || {};

    const buildFundingRows = (depositRows) => {
        const fundingMap = new Map();
        (depositRows || []).forEach(r => {
            const ukgId = r['Employee Id'] || '';
            const adpId = r['ADP ID'] || '';
            const entity = r['CC1'] || '';
            const ukgAccountName = r['Username'] || '';
            const code = (r['E/D/T Code'] || 'COMM').toString().toUpperCase();
            const amount = Number(r['E/D/T Amount'] || 0);
            if (amount === 0) return;

            const mapKey = `${entity}||${ukgAccountName}||${code}`;
            if (!fundingMap.has(mapKey)) {
                const meta = getMeta(ukgId, adpId);
                const coCode = coCodeMap.get(`adp:${adpId}`) || coCodeMap.get(`ukg:${ukgId}`) || meta.companyCode || '';
                let payType = meta.data?.payType || meta.payType || '';
                if (payType?.includes('Smile Leasing')) payType = 'W2';
                const specialty = (meta.data?.specialty || meta.specialty || meta.role || (meta.contractType === 'Ortho' ? 'Orthodontist' : '')).toString().toUpperCase();

                fundingMap.set(mapKey, {
                    'Batch Name': 'CDP Commissions',
                    'UKG ID': ukgId,
                    'ADP ID': adpId,
                    'PROVIDER': r['Username'] || '',
                    'Cash/Non-cash': 'Cash',
                    'Entity': entity,
                    'Sage Entity Name': '',
                    'Pay Amt': '0.00',
                    'UKG Account Name': ukgAccountName,
                    'PRACTICE': r['PRACTICE'] || '',
                    '_amount': 0
                });
            }
            const row = fundingMap.get(mapKey);
            row._amount += amount;
            row['Pay Amt'] = (row._amount).toFixed(2);
        });
        return Array.from(fundingMap.values()).map(r => {
            const { _amount, ...cleaned } = r;
            return cleaned;
        });
    };

    return {
        fundingData1: buildFundingRows(depositRows1),
        fundingData2: buildFundingRows(depositRows2)
    };
}

/**
 * Build UKG Page / UKG Export rows from already-computed DEPOSIT sheet rows.
 * 1ST DEPOSIT and 2ND DEPOSIT will be identical to the DEPOSIT 1ST / DEPOSIT 2ND sheets.
 * Rows are aggregated per provider + UKG code so one combined row is produced per code.
 *
 * @param {Array}  depositRows1   - rows already formatted for DEPOSIT 1ST (have 'E/D/T Code', 'E/D/T Amount', 'Employee Id', 'ADP ID', 'Username')
 * @param {Array}  depositRows2   - rows already formatted for DEPOSIT 2ND (same shape)
 * @param {Array}  providerData   - original provider data items (for PAY TYPE, SPECIALITY, PAY MONTH metadata)
 * @param {string} startDate
 * @param {Map}    coCodeMap      - Map keyed 'adp:<id>' / 'ukg:<id>' → companyCode
 */
function depositRowsToUkgRows(depositRows1, depositRows2, providerData, startDate, coCodeMap = new Map()) {
    const providerByUkg = new Map();
    const providerByAdp = new Map();
    (providerData || []).forEach(item => {
        if (!item) return;
        if (item.ukgid) providerByUkg.set(String(item.ukgid), item);
        if (item.adpId) providerByAdp.set(String(item.adpId), item);
    });
    const getMeta = (ukgId, adpId) =>
        providerByUkg.get(String(ukgId || '')) || providerByAdp.get(String(adpId || '')) || {};

    const ukgMap = new Map();
    const addRows = (rows, depositKey) => {
        (rows || []).forEach(r => {
            const ukgId = r['Employee Id'] || '';
            const adpId = r['ADP ID'] || '';
            const code = (r['E/D/T Code'] || 'COMM').toString().toUpperCase();
            const locationId = r['CC1'] || '';
            const amount = Number(r['E/D/T Amount'] || 0);
            if (amount === 0) return;
            // Include locationId (CC1) in the key so each practice gets its own UKG row,
            // mirroring the per-practice rows produced by the DEPOSIT 1ST / DEPOSIT 2ND sheets.
            const mapKey = `${ukgId}||${adpId}||${code}||${locationId}`;
            if (!ukgMap.has(mapKey)) {
                const meta = getMeta(ukgId, adpId);
                const coCode = coCodeMap.get(`adp:${adpId}`) || coCodeMap.get(`ukg:${ukgId}`) || meta.companyCode || '';
                let payType = meta.data?.payType || meta.payType || '';
                if (payType?.includes('Smile Leasing')) payType = 'W2';
                const specialty = (meta.data?.specialty || meta.specialty || meta.role || (meta.contractType === 'Ortho' ? 'Orthodontist' : '')).toString().toUpperCase();
                ukgMap.set(mapKey, {
                    'PROVIDER': r['Username'] || '',
                    'ADP ID': adpId,
                    'UKG ID': ukgId,
                    'CO CODE': coCode,
                    'UKG CODE': code,
                    'PAY TYPE': payType,
                    'SPECIALITY': specialty,
                    'PAY MONTH': meta.payMonth || (startDate ? moment(startDate).format('MMMM YYYY') : ''),
                    '1ST DEPOSIT': 0,
                    '2ND DEPOSIT': 0,
                    'AMOUNT': 0
                });
            }
            const ex = ukgMap.get(mapKey);
            ex[depositKey] = (ex[depositKey] || 0) + amount;
            ex['AMOUNT'] = (ex['AMOUNT'] || 0) + amount;
        });
    };
    addRows(depositRows1, '1ST DEPOSIT');
    addRows(depositRows2, '2ND DEPOSIT');

    return Array.from(ukgMap.values())
        .map(r => ({
            ...r,
            '1ST DEPOSIT': Number(r['1ST DEPOSIT'] || 0).toFixed(2),
            '2ND DEPOSIT': Number(r['2ND DEPOSIT'] || 0).toFixed(2),
            'AMOUNT': Number(r['AMOUNT'] || 0).toFixed(2)
        }))
        .sort((a, b) => String(a['UKG ID'] || '').localeCompare(String(b['UKG ID'] || '')));
}

function getAdpPayDates(startDate) {
    // Pay dates are the 2nd and 4th Fridays of the month following startDate
    let friday = moment(startDate).add(1, 'M').startOf('month').day('Friday');
    if (friday.date() > 7) friday.add(7, 'd');
    const month = friday.month();
    const fridays = [];
    while (friday.month() === month) {
        fridays.push(friday.clone());
        friday.add(7, 'd');
    }
    return [
        fridays[1] ? fridays[1].format('MMDDYYYY') : '',
        fridays[3] ? fridays[3].format('MMDDYYYY') : ''
    ];
}

// Build detailed transaction rows from detailedTransactionData arrays
// All transactions (base + financial notes) routed by date: 1-15 to D1, 16-31 to D2
// Exclude providers who should not appear in CDP reports (Hygienists and altPay)
const isExcludedProvider = (p) => {
    if (!p) return false;
    // Exclude altPay providers using canonical helper (handles nested shapes)
    try {
        if (getAltPayFlag(p) === true) return true;
    } catch (e) { }

    // Exclude Hygienists (role or specialty)
    const role = (p.role || '').toString().toLowerCase();
    if (role === 'hygienists' || role === 'hygienist') return true;
    const specialty = ((p.dentistShiftSpecialty || p.speciality || p.specialty || '')).toString().toLowerCase();
    if (specialty && specialty.includes('hygien')) return true;
    return false;
};

const isHygienistProvider = (provider) => {
    if (!provider) return false;

    const role = String(provider.role || '').trim().toLowerCase();
    const specialty = String(
        provider.dentistShiftSpecialty || provider.speciality || provider.specialty || ''
    ).trim().toLowerCase();
    const contractType = String(provider.contractType || '').trim().toLowerCase();

    return role === 'hygienists'
        || role === 'hygienist'
        || specialty.includes('hygien')
        || contractType === 'ukg hyn'
        || contractType === 'tmphyn'
        || contractType === 'tmpdoc'
        || contractType === 'hygienist incentive';
};

const getCdpWorksheetRole = (provider) => {
    if (isHygienistProvider(provider)) return 'Hygienists';
    return provider?.role || '';
};

/**
 * Selects providers for the first CDP Report worksheet.
 *
 * Hygienists intentionally remain in this dataset so the level 1/2/3 rows and
 * Total Pay column reconcile with the CDP report UI. Payroll-facing worksheets
 * continue to use the stricter filteredData dataset below.
 */
export const filterCdpWorksheetData = (data = []) => {
    return (data || []).filter(item => {
        if (!item || item.contractType === 'TMPDOC') return false;

        try {
            if (getAltPayFlag(item) === true) return false;
        } catch (e) { }

        const hasFinancialNotes = Array.isArray(item.financialNoteList)
            && item.financialNoteList.length > 0;
        const hasProductionData = Number(item.price || 0) > 0
            || Number(item.bCare || 0) > 0
            || Number(item.totalPay || 0) > 0
            || Number(item.hynBillableCare || 0) > 0
            || Object.values(item.data || {}).some(practice => (
                Number(practice?.price || 0) > 0
                || Number(practice?.practiceBcare || practice?.practiceBillableCare || 0) > 0
                || Number(practice?.hynBillableCare || 0) > 0
                || Number(practice?.procBasedPay || 0) !== 0
            ));
        const pay = Number(item.procBasedPay || 0);

        if (pay === 0 && !hasFinancialNotes && !(isHygienistProvider(item) && hasProductionData)) return false;
        if (pay < 0 && !isHygienistProvider(item)) return false;

        return true;
    });
};


const getCollectionPrice = (collectionsData, ABBR, iPracticeName) =>
    collectionsData?.[ABBR]?.data?.[iPracticeName]?.price ?? "";

const getGxGzColValue = (contractType, data, iPracticeName, ABBR, iDate) => {
    const practiceData = data?.[iPracticeName];
    if (!practiceData) return "";

    if (contractType === "Collections" || contractType === "Collections w/HYN" || contractType === "Collections Offset") {
        const collectionsKey = `${ABBR}_${iDate}`;
        return practiceData.sumCollectionsTotalByDate?.[collectionsKey]?.Amount ?? 0;
    }

    if (contractType === "GOZ" || contractType === "GOX")
        return practiceData.groupedGoxListByDate?.[iDate]?.goxNonBillableCare ?? 0;
    return "";
};

// Base contracts where the Base / Over Base status applies (mirrors the
// CdpReports "otherContracts" set). Sum Of, UKG-HYN, hygienists, etc. have no
// over-base concept and get no label.
const baseOverbaseContracts = new Set([
    'Collections', 'GOX', 'GOZ', 'Greater Of', 'Greater Of w/HYN',
    'Collections w/HYN', 'GOX w/HYN', 'Collections Offset'
]);

// Provider-level Base / Over Base status label for the CDP worksheet. Keyed off
// the base-contract contractType + the persisted isProviderOverBase flag, the
// same way the History Report column derives it. displayBasePay is intentionally
// NOT used as a gate: it is absent from saved S3 reports (the Go service reads
// those), so gating on it would blank the column. A null flag means over-base
// does not apply, so the cell stays blank.
const getBaseOverbaseLabel = (iRecord) => {
    if (!iRecord || !baseOverbaseContracts.has(iRecord.contractType)) return '';
    if (iRecord.isProviderOverBase == null) return '';
    return iRecord.isProviderOverBase ? 'Over Base' : 'Base Pay';
};

const createLevel1Record = (iRecord, collectionsData) => {
    const { ABBR, contractType, payMonth, payOverBase, payType, ukgid, adpId } = iRecord;
    const role = getCdpWorksheetRole(iRecord);
    const tierFlag = iRecord.isProviderOnTierBonus ? ' (TIER)' : '';
    return {
        level: 1, Provider: getDoctorNameWithPrefix(iRecord.doctorName), ABBR: iRecord.ABBR, Practice: "TBA",
        contractType: iRecord.contractType + tierFlag, "Work Time Type": iRecord.dentistWorkTimeType,
        "Worked Days": iRecord.numberOfWorkedDays, "Scheduled Days": iRecord.totalScheduledDays ?? iRecord.numberOfScheduledDays,
        RTOApproved: "", FTOApproved: "", "Patient Visits": iRecord.patientVisits,
        bCPV: Number(iRecord.bCPV || 0).toFixed(3), "PT Visits/Day": iRecord.patientVisitsPerDay,
        "Ramp Start Date": "", "Ramp End Date": "", "Procedure Date": "", "Start-End Date": "",
        Care: iRecord.price, WriteOffs: iRecord.writeOffPrice * -1, Adj: iRecord.adjustmentPrice,
        "Care Fraction": iRecord.prodAdjWo, "Gx-Gz-Col": iRecord?.GoxNonBillableCare?.toFixed(3) ?? "",
        gx: "", gz: "", col: "", bCare: Number(iRecord.bCare || 0).toFixed(3), "Financial Note": iRecord.financialNotes,
        "Financial Note - bCare": iRecord.billableNotes, "Total Pay": iRecord.procBasedPay,
        "% of Billable": iRecord.normalizedPercentageOfBillableCare, payMonth: payMonth ?? "",
        firstDeposit: iRecord.firstDeposit ?? "", secondDeposit: iRecord.secondDeposit ?? "",
        dentistType: iRecord.dentistType ?? "", isActive: iRecord.isActive ?? "",
        isProviderEligibleForBonus: iRecord.isProviderEligibleForBonus ?? "",
        isProviderOverBase: iRecord.isProviderOverBase ?? "", payOverBase: payOverBase ?? "",
        payType: payType ?? "", role: role ?? "", ukgid: ukgid ?? "", adpId: adpId ?? '',
        goxTotal: contractType === "GOX" || contractType === "GOZ" ? iRecord.GoxNonBillableCare : "",
        collections: contractType === "Collections" || contractType === "Collections w/HYN" ? collectionsData[ABBR]?.price : "",
        "HYN bCare": iRecord.hynBillableCare || "", "HYN Collections": "", "Adj/WO": iRecord.writeOffType,
        "Base Pay": iRecord.displayBasePay ?? iRecord.totalBasePay,
        "Base/Overbase": getBaseOverbaseLabel(iRecord),
        "GO Category": iRecord.greater_of_category ?? "",
    };
};

const createLevel2Record = (iRecord, practiceObject, iPracticeName, collectionsData) => {
    const { ABBR, contractType, payMonth, payOverBase, payType, ukgid, dentistType, isActive, isProviderOverBase, adpId } = iRecord;
    const role = getCdpWorksheetRole(iRecord);
    const practiceBillableCarePerVisit = practiceObject.patientVisits !== 0 ? practiceObject.practiceBillableCare / practiceObject.patientVisits : 0;
    const tierFlag = iRecord.isProviderOnTierBonus ? ' (TIER)' : '';

    return {
        level: 2, Provider: getDoctorNameWithPrefix(iRecord.doctorName), ABBR, Practice: practiceObject.practiceInfo[0].practice, contractType: contractType + tierFlag,
        "Work Time Type": iRecord.dentistWorkTimeType, "Worked Days": practiceObject.worked, "Scheduled Days": practiceObject.scheduled,
        RTOApproved: "", FTOApproved: "", "Patient Visits": practiceObject.patientVisits, bCPV: practiceBillableCarePerVisit,
        "PT Visits/Day": iRecord.patientVisitsPerDay, "Ramp Start Date": "", "Ramp End Date": "", "Procedure Date": "", "Start-End Date": "",
        Care: practiceObject.price, WriteOffs: practiceObject.writeOffPrice * -1, Adj: practiceObject.adjustmentPrice,
        "Care Fraction": iRecord.prodAdjWo,
        "Gx-Gz-Col": contractType === "Collections" || contractType === "Collections w/HYN" || contractType === "Collections Offset" ? iRecord.data[iPracticeName].collectionsNonBillableCare ?? 0 : contractType === "GOZ" || contractType === "GOX" ? iRecord.data[iPracticeName].goxNonBillableCare ?? 0 : "0",
        gx: iRecord.data[iPracticeName].goxNonBillableCare ?? 0, gz: iRecord.data[iPracticeName].goxNonBillableCare ?? 0,
        col: iRecord.data[iPracticeName].collectionsNonBillableCare ?? 0, bCare: practiceObject?.practiceBcare?.toFixed(3),
        "Financial Note": practiceObject?.financialNotes, "Financial Note - bCare": practiceObject?.billableNotes,
        "Total Pay": practiceObject?.practiceTotalPay ?? practiceObject?.procBasedPay, "% of Billable": practiceObject?.practicePercentageOfBillable,
        payMonth, firstDeposit: "", secondDeposit: "", dentistType, isActive, isProviderEligibleForBonus: practiceObject > 0,
        isProviderOverBase, payOverBase, payType, role, ukgid, adpId,
        goxTotal: contractType === "GOX" || contractType === "GOZ" ? practiceObject.goxNonBillableCare : "",
        collections: getCollectionPrice(collectionsData, ABBR, iPracticeName),
        "HYN bCare": iRecord.data[iPracticeName]?.hynBillableCare || "", "HYN Collections": practiceObject?.pracHynCollections,
        "Adj/WO": iRecord.writeOffType,
        "Base Pay": practiceObject?.practicePayBasedOnDailyRate || '',
        "Base/Overbase": "",
        "GO Category": iRecord.greater_of_category ?? "",
        "Pay Override": practiceObject?.hasOverride ? "Yes" : "",
    };
};

const createLevel3Record = (iRecord, practiceObject, dateObject, iDate, iPracticeName, collectionsData, scheduleHours) => {
    const { ABBR, contractType, payMonth, payOverBase, payType, ukgid, dentistType, isActive, isProviderOverBase, adpId } = iRecord;
    const role = getCdpWorksheetRole(iRecord);
    const practiceName = (practiceObject.practiceInfo[0].practice || iPracticeName || "").toLowerCase();
    const startEnd = scheduleHours[practiceName]?.filter(shift => moment(shift.startDate).utc().format("YYYY-MM-DD") === iDate && shift.abbr.toLowerCase().includes(ABBR.toLowerCase())) || [];
    const practiceDayBCare = dateObject?.billableCare || 0;
    const bPCV = dateObject?.patientVisits ? (practiceDayBCare + parseFloat(dateObject?.billableNotes || 0)) / dateObject.patientVisits : 0;
    const practiceHynData = practiceObject.dataHyg || {};
    const hygAbbr = practiceObject?.hygABRR;
    const practiceHynBCare = practiceHynData[iDate]?.billableCare || 0;
    const pracHynCollections = practiceHynData[`${hygAbbr}_${iDate}`]?.Amount || 0;
    const tierFlag = iRecord.isProviderOnTierBonus ? ' (TIER)' : '';

    return {
        level: 3, Provider: iRecord.doctorName, ABBR, Practice: practiceObject.practiceInfo[0].practice, contractType: contractType + tierFlag,
        "Work Time Type": "", "Worked Days": "", "Scheduled Days": "", RTOApproved: "", FTOApproved: "",
        "Patient Visits": dateObject.patientVisits, bCPV: bPCV.toFixed(3), "PT Visits/Day": "",
        "Ramp Start Date": "", "Ramp End Date": "", "Procedure Date": iDate,
        "Start-End Date": startEnd.length ? `${moment(parseInt(startEnd[0].start, 10)).utc().format("hh:mm A")} - ${moment(parseInt(startEnd[0].end, 10)).utc().format("hh:mm A")}` : "Not found",
        Care: dateObject.price?.toFixed(3), WriteOffs: dateObject.writeOffPrice * -1, Adj: dateObject.adjustmentPrice,
        "Care Fraction": iRecord.prodAdjWo, "Gx-Gz-Col": getGxGzColValue(contractType, iRecord.data, iPracticeName, ABBR, iDate),
        gx: practiceObject.groupedGoxListByDate?.[iDate]?.goxNonBillableCare ?? 0,
        gz: practiceObject.groupedGoxListByDate?.[iDate]?.goxNonBillableCare ?? 0,
        col: practiceObject.sumCollectionsTotalByDate?.[`${ABBR}_${iDate}`]?.Amount ?? 0,
        bCare: (practiceDayBCare + parseFloat(dateObject.billableNotes || 0)).toFixed(3),
        "Financial Note": dateObject?.financialNotes?.toFixed(3), "Financial Note - bCare": dateObject?.billableNotes?.toFixed(3),
        "Total Pay": dateObject.totalPay, "% of Billable": "", payMonth, firstDeposit: "", secondDeposit: "",
        dentistType, isActive, isProviderEligibleForBonus: practiceObject > 0, isProviderOverBase,
        payOverBase, payType, role, ukgid, adpId,
        goxTotal: contractType === "GOX" || contractType === "GOZ" ? practiceObject.goxNonBillableCare : "",
        collections: getCollectionPrice(collectionsData, ABBR, iPracticeName),
        "HYN bCare": practiceHynBCare ? practiceHynBCare + (practiceHynData[iDate]?.billableNotes || 0) : "",
        "HYN Collections": pracHynCollections ? parseFloat(pracHynCollections).toFixed(3) : "",
        "Adj/WO": iRecord.writeOffType,
        "Base Pay": dateObject.patientVisits > 0 && practiceObject.basePayByDay ? practiceObject.basePayByDay : '',
        "Base/Overbase": "",
        "GO Category": iRecord.greater_of_category ?? "",
    };
};

export const flattenNestedRecords = (records = [], collectionsData = {}, scheduleHours = {}) => {
    return records.flatMap(iRecord => {
        const level1 = createLevel1Record(iRecord, collectionsData);
        const practiceRecords = Object.entries(iRecord.data ?? {}).flatMap(([iPracticeName, practiceObject]) => {
            const level2 = createLevel2Record(iRecord, practiceObject, iPracticeName, collectionsData);
            const dateRecords = Object.entries(practiceObject?.data ?? {}).map(([iDate, dateObject]) =>
                createLevel3Record(iRecord, practiceObject, dateObject, iDate, iPracticeName, collectionsData, scheduleHours)
            );
            return [level2, ...dateRecords];
        });
        return [level1, ...practiceRecords];
    });
};

export const exportAltPayCompleteReport = async (data, startDate, endDate, getDoctorFullName) => {
    try {
        const workbook = XLSX.utils.book_new();

        // UKG Report Sheet
        const ukgData = data.filter(item => item?.contractType !== 'UKG HYN' && item?.contractType !== 'TMPDOC' && item?.contractType !== 'TMPHYN' && item?.procBasedPay !== 0)
            .map(item => ({
                'PROVIDER': getDoctorNameWithPrefix(item.doctorName),
                'ADP ID': item.adpId,
                'UKG ID': item.ukgid,
                'UKG CODE': item.payType === '1099' ? '1099' : item.ukgCode,
                'PAY TYPE': item.payType,
                'SPECIALITY': item.role,
                'PAY DATE': exportDates.payDate,
                'AMOUNT': Number(item.procBasedPay || 0).toFixed(2)
            }));
        const ukgSheet = XLSX.utils.json_to_sheet(ukgData);
        XLSX.utils.book_append_sheet(workbook, ukgSheet, `UKG ${exportDates.exportPayDate}`);

        // 2. Funding Sheet
        const altPayFundingData = getAltPayFundingOrDepositData(data, 'first', startDate, true)
            .map(item => ({
                'Batch Name': 'CDP Commissions',
                'UKG ID': item.ukgid,
                'ADP ID': item.adpId,
                'PROVIDER': getDoctorNameWithPrefix(item.doctorName),
                'Cash/Non-cash': 'Cash',
                'Entity': item.sageId || '',
                'Sage Entity Name': item.sageEntityName || '',
                'Pay Amt': Number(item.amount || 0).toFixed(2),
                'UKG Account Name': item.ukgAccountName,
                'PRACTICE': item.practice || ''
            }));
        const fundingSheet = XLSX.utils.json_to_sheet(altPayFundingData);
        XLSX.utils.book_append_sheet(workbook, fundingSheet, `FUNDING ${exportDates.exportPayDate}`);

        // Sage Report Sheet - Using the reusable method
        const sageData = prepareSageExportData(data, getDoctorFullName, startDate, true);
        const sageExcelData = convertSageDataToExcelFormat(sageData, true);
        const sageSheet = XLSX.utils.json_to_sheet(sageExcelData);
        XLSX.utils.book_append_sheet(workbook, sageSheet, `Sage Export ${exportDates.exportPayDate}`);

        //  Deposit Sheet 
        const altPayDepositData = getAltPayFundingOrDepositData(data, 'first', startDate, true)
            .map(item => ({
                'Company Short Name': '6174247',
                'Employee Id': item.ukgid,
                'ADP ID': item.adpId || '',
                'Username': getDoctorNameWithPrefix(item.doctorName),
                'Badge Id': '',
                'SSN#': '',
                'Employee External Id': '',
                'Pay Statement #': '',
                'Check #': '',
                'Pay Period Start Date': '',
                'Pay Period End Date': '',
                'Pay Statement Type': 'REGULAR',
                'Pay Statement Type Key': '',
                'Pay Order': '',
                'Start Date': '',
                'End Date': '',
                'Start Time': '',
                'End Time': '',
                'E/D/T': 'E',
                'E/D/T Code': item.ukgCode,
                'E/D/T Name': '',
                'E/D/T Abbrev': '',
                'E/D/T External ID': '',
                'E Hours': '',
                'E Pieces': '',
                'E Base Rate': '',
                'E Shift': '',
                'E/D/T Amount': Number(item.amount || 0).toFixed(2),
                'WC Code': '',
                'T EE Gross Wages': '',
                'T ER Gross Wages': '',
                'T EE Gross Subject Wages': '',
                'T ER Gross Subject Wages': '',
                'T EE Subject Wages': '',
                'T ER Subject Wages': '',
                'D/T Employer Amt': '',
                'CC1': item.sageId || '',
                'CC2': '',
                'CC3': '',
                'CC4': '',
                'CC5': '',
                'D Vendor': '',
                'Mode': '',
                'Count Zeroes': ''
            }));
        const depositSheet = XLSX.utils.json_to_sheet(altPayDepositData);
        XLSX.utils.book_append_sheet(workbook, depositSheet, `DEPOSIT ${exportDates.exportPayDate}`);

        // 5. Alt Pay Report Sheet
        const altPayData = data.map(item => {
            const baseRecord = {
                level: 1,
                Provider: getDoctorNameWithPrefix(item.doctorName),
                ABBR: item.ABBR,
                Practice: 'TBA',
                contractType: item.contractType,
                "Work Time Type": item.dentistWorkTimeType,
                "Worked Days": item.numberOfWorkedDays,
                "Scheduled Days": item.totalScheduledDays ?? item.numberOfScheduledDays,
                RTOApproved: '',
                FTOApproved: '',
                "Patient Visits": item.patientVisits,
                bCPV: item.bCPV?.toFixed(3) || '',
                "PT Visits/Day": item.patientVisitsPerDay,
                "Ramp Start Date": '',
                "Ramp End Date": '',
                "Procedure Date": '',
                "Start-End Date": '',
                Care: item.price,
                WriteOffs: item.writeOffPrice,
                Adj: item.adjustmentPrice,
                "Care Fraction": item.prodAdjWo,
                "Gx-Gz-Col": item.GoxNonBillableCare?.toFixed(3) || '',
                gx: '',
                gz: '',
                col: '',
                bCare: item.bCare?.toFixed(3) || '',
                "Financial Note": item.financialNotes,
                "Total Pay": item.procBasedPay,
                "% of Billable": item.normalizedPercentageOfBillableCare,
                payMonth: item.payMonth || '',
                firstDeposit: item.firstDeposit || '',
                secondDeposit: item.secondDeposit || '',
                dentistType: item.dentistType || '',
                isActive: item.isActive || '',
                isProviderEligibleForBonus: item.isProviderEligibleForBonus || '',
                isProviderOverBase: item.isProviderOverBase || '',
                payOverBase: item.payOverBase || '',
                payType: item.payType || '',
                role: item.role || '',
                ukgid: item.ukgid || '',
                adpId: item.adpId || '',
                goxTotal: (item.contractType === "GOX" || item.contractType === "GOZ") ? item.GoxNonBillableCare : '',
                collections: '',
                "HYN bCare": item.hynBillableCare || '',
                "HYN Collections": ''
            };

            const practiceRecords = [];
            if (item.data) {
                Object.entries(item.data).forEach(([practiceName, practiceData]) => {
                    const practiceRecord = {
                        level: 2,
                        Provider: item.doctorName,
                        ABBR: item.ABBR,
                        Practice: practiceData.practiceInfo?.[0]?.practice || practiceName,
                        contractType: item.contractType,
                        "Work Time Type": item.dentistWorkTimeType,
                        "Worked Days": practiceData.worked,
                        "Scheduled Days": practiceData.scheduled,
                        RTOApproved: '',
                        FTOApproved: '',
                        "Patient Visits": practiceData.patientVisits,
                        bCPV: practiceData.practiceBillableCare ? (practiceData.practiceBillableCare / practiceData.patientVisits).toFixed(3) : '',
                        "PT Visits/Day": item.patientVisitsPerDay,
                        "Ramp Start Date": '',
                        "Ramp End Date": '',
                        "Procedure Date": '',
                        "Start-End Date": '',
                        Care: practiceData.price,
                        WriteOffs: practiceData.writeOffPrice,
                        Adj: practiceData.adjustmentPrice,
                        "Care Fraction": item.prodAdjWo,
                        "Gx-Gz-Col": practiceData.collectionsNonBillableCare || practiceData.goxNonBillableCare || '',
                        gx: practiceData.goxNonBillableCare || 0,
                        gz: practiceData.goxNonBillableCare || 0,
                        col: practiceData.collectionsNonBillableCare || 0,
                        bCare: practiceData.practiceBcare?.toFixed(3) || '',
                        "Financial Note": practiceData.financialNotes,
                        "Total Pay": practiceData.procBasedPay,
                        "% of Billable": practiceData.practicePercentageOfBillable,
                        payMonth: item.payMonth,
                        firstDeposit: '',
                        secondDeposit: '',
                        dentistType: item.dentistType,
                        isActive: item.isActive,
                        isProviderEligibleForBonus: practiceData > 0,
                        isProviderOverBase: item.isProviderOverBase,
                        payOverBase: item.payOverBase,
                        payType: item.payType,
                        role: item.role,
                        ukgid: item.ukgid,
                        adpId: item.adpId,
                        goxTotal: (item.contractType === "GOX" || item.contractType === "GOZ") ? practiceData.goxNonBillableCare : '',
                        collections: '',
                        "HYN bCare": item.data?.[practiceName]?.hynBillableCare || '',
                        "HYN Collections": practiceData.pracHynCollections || ''
                    };
                    practiceRecords.push(practiceRecord);

                    // Add daily records
                    if (practiceData.data) {
                        Object.entries(practiceData.data).forEach(([date, dateData]) => {
                            const dailyRecord = {
                                level: 3,
                                Provider: item.doctorName,
                                ABBR: item.ABBR,
                                Practice: practiceData.practiceInfo?.[0]?.practice || practiceName,
                                contractType: item.contractType,
                                "Work Time Type": '',
                                "Worked Days": '',
                                "Scheduled Days": '',
                                RTOApproved: '',
                                FTOApproved: '',
                                "Patient Visits": dateData.patientVisits,
                                bCPV: dateData.billableCare ? ((dateData.billableCare + parseFloat(dateData.billableNotes || 0)) / dateData.patientVisits).toFixed(3) : '',
                                "PT Visits/Day": '',
                                "Ramp Start Date": '',
                                "Ramp End Date": '',
                                "Procedure Date": date,
                                "Start-End Date": '',
                                Care: dateData.price?.toFixed(3) || '',
                                WriteOffs: dateData.writeOffPrice,
                                Adj: dateData.adjustmentPrice,
                                "Care Fraction": item.prodAdjWo,
                                "Gx-Gz-Col": practiceData.sumCollectionsTotalByDate?.[`${item.ABBR}_${date}`]?.Amount || practiceData.groupedGoxListByDate?.[date]?.goxNonBillableCare || '',
                                gx: practiceData.groupedGoxListByDate?.[date]?.goxNonBillableCare || 0,
                                gz: practiceData.groupedGoxListByDate?.[date]?.goxNonBillableCare || 0,
                                col: practiceData.sumCollectionsTotalByDate?.[`${item.ABBR}_${date}`]?.Amount || 0,
                                bCare: (dateData.billableCare + parseFloat(dateData.billableNotes || 0)).toFixed(3),
                                "Financial Note": dateData.financialNotes?.toFixed(3) || '',
                                "Total Pay": dateData.totalPay,
                                "% of Billable": '',
                                payMonth: item.payMonth,
                                firstDeposit: '',
                                secondDeposit: '',
                                dentistType: item.dentistType,
                                isActive: item.isActive,
                                isProviderEligibleForBonus: practiceData > 0,
                                isProviderOverBase: item.isProviderOverBase,
                                payOverBase: item.payOverBase,
                                payType: item.payType,
                                role: item.role,
                                ukgid: item.ukgid,
                                adpId: item.adpId,
                                goxTotal: (item.contractType === "GOX" || item.contractType === "GOZ") ? practiceData.goxNonBillableCare : '',
                                collections: '',
                                "HYN bCare": practiceData.dataHyg?.[date]?.billableCare || '',
                                "HYN Collections": practiceData.dataHyg?.[`${practiceData.hygABRR}_${date}`]?.Amount?.toFixed(3) || ''
                            };
                            practiceRecords.push(dailyRecord);
                        });
                    }
                });
            }

            return [baseRecord, ...practiceRecords];
        }).flat();

        const altPaySheet = XLSX.utils.json_to_sheet(altPayData);
        XLSX.utils.book_append_sheet(workbook, altPaySheet, `Alt Pay Report ${exportDates.exportPayDate}`);

        // New Financial Notes Sheet
        const financialNotesData = data.flatMap(item => {
            // Use the financialNoteList directly from the item
            if (item.financialNoteList && item.financialNoteList.length > 0) {
                // Exclude Account 2230 - these only belong in SAGE sheets
                return item.financialNoteList.filter(note => (note.sageAccount || '2230') !== '2230').map(note => ({
                    'Type': note.notesType || '',
                    'Category': note.category || '',
                    'Title': note.title || '',
                    'Provider': getDoctorNameWithPrefix(item.doctorName),
                    'Practice': note.practice || '',
                    'Description': note.desc || '',
                    'Frequency': note.frequency || '',
                    'Taxable': note.isTaxable ? 'Yes' : 'No',
                    'Account': note.sageAccount || '',
                    'Amount': Number(note.financialNotes || 0).toFixed(2),
                    'B.Care': note.bcare || '',
                    'Date': note.dateString || '',
                    'Published': 'Yes'
                }));
            }
            return [];
        });

        const financialNotesSheet = XLSX.utils.json_to_sheet(financialNotesData);
        XLSX.utils.book_append_sheet(workbook, financialNotesSheet, `Financial Notes ${exportDates.exportPayDate}`);

        // Patient Codes Sheet
        const patientCodesData = [];

        const patientCodesSheet = XLSX.utils.json_to_sheet(patientCodesData);
        XLSX.utils.book_append_sheet(workbook, patientCodesSheet, `Patient Codes`);

        // Generate and download the file
        const excelBuffer = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array'
        });

        const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Alt Pay Complete Report ${exportDates.exportPayDate}.xlsx`;
        link.click();

        // Cleanup
        URL.revokeObjectURL(link.href);

    } catch (error) {
        throw error;
    }
};

export const exportCdpCompleteReport = async (data, collectionsData, scheduleHours, startDate, endDate, getDoctorFullName, { getPracticeName, providerRates, cdpWorksheetSource }, onProgress, singleSheetConfig = null, isIndividual = false) => {
    // CRITICAL (Part 1): Generate the Financial Notes from the original, unfiltered data.
    // This ensures that hygienist wages and other special notes are always included, matching the master report.
    // Exclude Account 2230 - these only belong in SAGE sheets
    const financialNotesData = (data || []).flatMap(item => {
        return item.financialNoteList?.filter(note => (note.sageAccount || '2230') !== '2230').map(note => ({
            'Type': note.notesType || '',
            'Category': note.category || '',
            'Title': note.title || '',
            'Provider': getDoctorNameWithPrefix(item.doctorName),
            'Practice': note.practice || '',
            'Description': note.desc || '',
            'Frequency': note.frequency || '',
            'Taxable': note.isTaxable ? 'Yes' : 'No',
            'Account': note.sageAccount || '',
            'UKG Code': note.ukg || '',
            'Amount': Number(note.financialNotes || 0).toFixed(2),
            'B.Care': note.bcare || '',
            'Date': note.dateString || '',
            'Published': 'Yes'
        })) ?? [];
    });


    // CRITICAL: Apply the canonical filtering logic at the very beginning.
    // This ensures that all subsequent sheets (CDP, UKG, SAGE, etc.) are generated
    // from the exact same, fully-filtered dataset, matching the master report.
    const filteredData = (data || []).filter(item => {
        if (!item) return false;
        if (item.contractType === 'UKG HYN' || item.contractType === 'TMPDOC' || item.contractType === 'TMPHYN') return false;
        // Keep providers that have financial notes even if procBasedPay is zero so COMM rows can be generated
        if (Number(item.procBasedPay || 0) === 0 && !(item.financialNoteList && item.financialNoteList.length > 0)) return false;
        // Exclude providers with negative pay, which was the source of the mismatch.
        if (Number(item.procBasedPay || 0) < 0) return false;
        // Exclude altPay providers
        try {
            if (getAltPayFlag(item) === true) return false;
        } catch (e) { }
        return true;
    });

    const workbook = XLSX.utils.book_new();

    // Format dates for display
    const payDate = moment(startDate).format('YYYY-MMM');


    // The first worksheet is an analysis view and includes all paid hygienists.
    // Other payroll-facing worksheets continue to use filteredData.
    const cdpWorksheetData = filterCdpWorksheetData(cdpWorksheetSource || data);
    const cdpData = flattenNestedRecords(cdpWorksheetData, collectionsData, scheduleHours, getPracticeName);

    XLSXaddSheet(workbook, cdpData, `CDP Report ${moment(startDate).format("Y-MMM")}`);

    // Sage Report Page - Filter during transformation
    const sagePageData = getSageReportCalculatedData(startDate, filteredData, getDoctorFullName, false);
    const filteredsagePageData = sagePageData.filter(item => (
        item?.procBasedPay !== 0 && item?.contractType !== 'TMPDOC' && item?.contractType !== 'TMPHYN'
    ));
    const sagePageSheetData = filteredsagePageData.map((item, index) => ({
        'PROVIDER': getDoctorNameWithPrefix(item.doctor || getDoctorFullName(item.ABBR)),
        'PRACTICE': item.practice,
        'PAY TYPE': item.payType ? item.payType : '',
        'SPECIALITY': item.role ? item.role : '',
        'Pay Month': item.payMonth,
        'Financial Note': item.finNote ? Number(item.finNote || 0).toFixed(2) : '',
        'Debit': item.debit ? Number(item.debit || 0).toFixed(2) : '',
        'ACCT No.': item.sageAccount,
        'Location ID': item.sageId,
    }));

    XLSXaddSheet(workbook, sagePageSheetData, `SAGE Page ${moment(startDate).format("Y-MMM")}`);

    // First Funding Sheet
    const fundingData1 = generateFundingDataByFundName(filteredData, 'first', false, startDate, false, getPracticeName)
    XLSXaddSheet(workbook, fundingData1, `FUNDING 1ST ${moment(startDate).format("Y-MMM")}`);

    // Second Funding Sheet
    const fundingData2 = generateFundingDataByFundName(filteredData, 'second', false, startDate, false, getPracticeName)
    XLSXaddSheet(workbook, fundingData2, `FUNDING 2ND ${moment(startDate).format("Y-MMM")}`);

    // Sage Report Sheet - Filter during transformation
    const sageData = getSageReportCalculatedData(startDate, filteredData, getDoctorFullName, false);
    const sageSheetData = sageData.map((item, index) => ({
        'PROVIDER': getDoctorNameWithPrefix(item.doctor || getDoctorFullName(item.ABBR)),
        'PRACTICE': item.practice,
        'DONOTIMPORT': '',
        'JOURNAL': 'DRPJ',
        'DATE': moment(startDate).endOf('M').format('MM/DD/YYYY'),
        'REVERSEDATE': '',
        'DESCRIPTION': 'CDP Payroll Journal -Provider',
        'REFERENCE_NO': 'CDP Payroll Journal -Provider',
        'LINE_NO': index + 1,
        'ACCT_NO': item.sageAccount,
        'LOCATION_ID': item.sageId,
        'DEPT_ID': 'COR_DOC_COMP',
        'DOCUMENT': '',
        'MEMO': item.doctor
            ? `Dr Accrual-${getDoctorNameWithPrefix(item.doctor)}`
            : `Dr Accrual-${moment(startDate).endOf('M').format('MM/DD/YYYY')}`,
        'DEBIT': item.debit,
        'CREDIT': '',
        'SOURCEENTITY': 'C_0002',
        'CURRENCY': '',
        'EXCH_RATE_DATE': '',
        'EXCH_RATE_TYPE_ID': '',
        'EXCHANGE_RATE': '',
        'STATE': '',
        'ALLOCATION_ID': '',
        'BILLABLE': '',
        'GLENTRY_CUSTOMERID': '',
        'GLENTRY_EMPLOYEEID': '',
        'GLENTRY_CLASSID': item.role === 'Oral Surgeon'
            ? 'OS'
            : (item.role === 'Dentist' || item.role === 'Hygienists')
                ? 'GENERAL'
                : 'GENERAL'
    }));

    // diagnostic downloads removed
    XLSXaddSheet(workbook, sageSheetData, `SAGE EXPORT ${moment(startDate).format("Y-MMM")}`);

    // Build Deposit sheets from canonical getDepositData (ensure they always appear)
    const canonicalFirstDeposits = getDepositData(filteredData, 'first', startDate, false) || [];
    const canonicalSecondDeposits = getDepositData(filteredData, 'second', startDate, false) || [];

    // Pre-build sageId → ADP cost-centre code so each deposit row can carry its own temp cost base.
    // Both sage_ID (Location ID) and adpId (temp cost base) live in the same customFields JSON, so we
    // key by customFields.sage_ID — exactly the same value the SAGE Page writes as "Location ID".
    // This is the authoritative source; practiceData.sageId is a different, less-reliably-populated field.
    const _cdpSageIdToTempCostBase = new Map();
    (filteredData || []).forEach(item => {
        Object.values(item.data || {}).forEach(practiceData => {
            try {
                const cf = practiceData?.practiceInfo?.[0]?.UdaPracticeInfos?.customFields;
                const parsed = cf ? JSON.parse(cf) : null;
                if (!parsed) return;
                // sage_ID is what the SAGE Page shows as Location ID — use it as the canonical key.
                // Fall back to sage_ID as the temp cost base when adpId is not configured, since they
                // represent the same per-practice location concept.
                const sageId = parsed.sage_ID || practiceData.sageId || '';
                if (!sageId) return;
                const tempCostBase = String(parsed.adpId || parsed.sage_ID || '');
                if (tempCostBase && !_cdpSageIdToTempCostBase.has(sageId)) {
                    _cdpSageIdToTempCostBase.set(sageId, tempCostBase);
                }
                // Also key by practiceData.sageId as a secondary alias when different
                const altId = practiceData.sageId || '';
                if (altId && altId !== sageId && tempCostBase && !_cdpSageIdToTempCostBase.has(altId)) {
                    _cdpSageIdToTempCostBase.set(altId, tempCostBase);
                }
            } catch (e) {}
        });
    });

    const toDepositRow = (item) => ({
        'Company Short Name': '6174247',
        'Employee Id': item.ukgid || '',
        'ADP ID': item.adpId || '',
        'Username': getDoctorNameWithPrefix(item.doctorName) || '',
        'Badge Id': '',
        'SSN#': '',
        'Employee External Id': '',
        'Pay Statement #': '',
        'Check #': '',
        'Pay Period Start Date': '',
        'Pay Period End Date': '',
        'Pay Statement Type': 'REGULAR',
        'Pay Statement Type Key': '',
        'Pay Order': '',
        'Start Date': '',
        'End Date': '',
        'Start Time': '',
        'End Time': '',
        'E/D/T': 'E',
        'E/D/T Code': item.ukgCode || 'COMM',
        'E/D/T Name': '',
        'E/D/T Abbrev': '',
        'E/D/T External ID': '',
        'E Hours': '',
        'E Pieces': '',
        'E Base Rate': '',
        'E Shift': '',
        'E/D/T Amount': Number(item.amount || 0).toFixed(2),
        'WC Code': '',
        'T EE Gross Wages': '',
        'T ER Gross Wages': '',
        'T EE Gross Subject Wages': '',
        'T ER Gross Subject Wages': '',
        'T EE Subject Wages': '',
        'T ER Subject Wages': '',
        'D/T Employer Amt': '',
        'CC1': item.sageId || '',
        'CC2': '',
        'CC3': '',
        'CC4': '',
        'CC5': '',
        'D Vendor': '',
        'Mode': '',
        'Count Zeroes': '',
        // Embedded at build time so depositRowsToAdpRows never needs an order-dependent map lookup
        '_tempCostBase': _cdpSageIdToTempCostBase.get(item.sageId || '') || ''
    });

    const depositData1 = canonicalFirstDeposits.filter(e => Number(e.amount || 0) !== 0).map(toDepositRow);
    depositData1.sort((a, b) => String(a['Employee Id'] || '').localeCompare(String(b['Employee Id'] || '')));

    const depositData2 = canonicalSecondDeposits.filter(e => Number(e.amount || 0) !== 0).map(toDepositRow);
    depositData2.sort((a, b) => String(a['Employee Id'] || '').localeCompare(String(b['Employee Id'] || '')));

    // Build CO CODE map from filtered provider data
    const cdpCompanyCodeMap = new Map();
    (filteredData || []).forEach(item => {
        if (item) {
            if (item.adpId) cdpCompanyCodeMap.set(`adp:${item.adpId}`, item.companyCode || '');
            if (item.ukgid) cdpCompanyCodeMap.set(`ukg:${item.ukgid}`, item.companyCode || '');
        }
    });

    // Build UKG from deposits — guarantees UKG 1ST DEPOSIT / 2ND DEPOSIT match DEPOSIT sheets exactly
    const cdpUkgData = depositRowsToUkgRows(depositData1, depositData2, filteredData, startDate, cdpCompanyCodeMap);

    // Add sheets in correct tab order: UKG Page → DEPOSIT 1ST → DEPOSIT 2ND → UKG EXPORT
    // Strip the internal _tempCostBase field before writing the DEPOSIT sheets
    XLSXaddSheet(workbook, cdpUkgData, `UKG Page ${moment(startDate).format("Y-MMM")}`);
    XLSXaddSheet(workbook, depositData1.map(({_tempCostBase: _t1, ...r}) => r), `DEPOSIT 1ST ${moment(startDate).format("Y-MMM")}`);
    XLSXaddSheet(workbook, depositData2.map(({_tempCostBase: _t2, ...r}) => r), `DEPOSIT 2ND ${moment(startDate).format("Y-MMM")}`);
    XLSXaddSheet(workbook, cdpUkgData, `UKG EXPORT ${moment(startDate).format("Y-MMM")}`);

    // ADP 1 and ADP 2 sheets — derived directly from depositData1/depositData2 so amounts are
    // guaranteed identical to the DEPOSIT 1ST / DEPOSIT 2ND sheets.
    const [adpDate1Cdp, adpDate2Cdp] = getAdpPayDates(startDate);

    // Per-practice temp cost base is now embedded directly in each deposit row (via toDepositRow + _cdpSageIdToTempCostBase).
    // This fallback map is only used for rows that carry no embedded _tempCostBase (e.g. empty sageId).
    const cdpTempCostMap = new Map();
    (filteredData || []).forEach(item => {
        if (!item.adpId) return;
        // Sort practice keys so the first valid adpId is always deterministic
        const sortedPracticeKeys = Object.keys(item.data || {}).sort();
        for (const practiceKey of sortedPracticeKeys) {
            const practiceData = item.data[practiceKey];
            try {
                const cf = practiceData?.practiceInfo?.[0]?.UdaPracticeInfos?.customFields;
                const parsed = cf ? JSON.parse(cf) : null;
                // Prefer adpId; fall back to sage_ID (same Location ID the SAGE page uses)
                const base = String(parsed?.adpId || parsed?.sage_ID || '');
                if (base) {
                    cdpTempCostMap.set(String(item.adpId), base);
                    break;
                }
            } catch (e) {}
        }
    });

    // Build allCostsMap: adpId → sorted array of all temp cost bases across all practices.
    // Used to populate fallback cells with all available options for analyst review.
    const cdpAllCostsMap = new Map();
    (filteredData || []).forEach(item => {
        if (!item.adpId) return;
        const costs = new Set();
        Object.values(item.data || {}).forEach(practiceData => {
            try {
                // Use customFields.sage_ID as canonical key (same as SAGE Page + _cdpSageIdToTempCostBase)
                const cf = practiceData?.practiceInfo?.[0]?.UdaPracticeInfos?.customFields;
                const parsed = cf ? JSON.parse(cf) : null;
                const canonicalId = (parsed?.sage_ID) || practiceData?.sageId || '';
                const base = canonicalId ? _cdpSageIdToTempCostBase.get(canonicalId) : undefined;
                if (base) costs.add(base);
            } catch (e) {
                const sageId = practiceData?.sageId || '';
                const base = sageId ? _cdpSageIdToTempCostBase.get(sageId) : undefined;
                if (base) costs.add(base);
            }
        });
        if (costs.size > 0) cdpAllCostsMap.set(String(item.adpId), [...costs].sort());
    });

    const _cdpAdpRows1 = depositRowsToAdpRows(depositData1, adpDate1Cdp, cdpCompanyCodeMap, cdpTempCostMap, cdpAllCostsMap);
    const _cdpAdpRows2 = depositRowsToAdpRows(depositData2, adpDate2Cdp, cdpCompanyCodeMap, cdpTempCostMap, cdpAllCostsMap);
    const _cdpAdp1FallbackIdx = _cdpAdpRows1.reduce((acc, r, i) => { if (r._isFallback) acc.push(i); return acc; }, []);
    const _cdpAdp2FallbackIdx = _cdpAdpRows2.reduce((acc, r, i) => { if (r._isFallback) acc.push(i); return acc; }, []);
    const _cdpAdpClean1 = _cdpAdpRows1.map(({ _isFallback: _f, ...r }) => r);
    const _cdpAdpClean2 = _cdpAdpRows2.map(({ _isFallback: _f, ...r }) => r);

    // Determine column letter for 'Temp Cost Number' from the generated sheet structure
    let _cdpAdpTcCol = 'I';
    if (_cdpAdpClean1.length > 0) {
        const _tmpWs2 = XLSX.utils.json_to_sheet(_cdpAdpClean1.slice(0, 1));
        const _r2 = XLSX.utils.decode_range(_tmpWs2['!ref'] || 'A1:A1');
        for (let c2 = _r2.s.c; c2 <= _r2.e.c; c2++) {
            const cell2 = _tmpWs2[XLSX.utils.encode_cell({ r: 0, c: c2 })];
            if (cell2 && cell2.v === 'Temp Cost Number') { _cdpAdpTcCol = XLSX.utils.encode_col(c2); break; }
        }
    }

    XLSXaddSheet(workbook, _cdpAdpClean1, `ADP 1 ${moment(startDate).format("Y-MMM")}`);
    XLSXaddSheet(workbook, _cdpAdpClean2, `ADP 2 ${moment(startDate).format("Y-MMM")}`);

    const financialNotesSheet = XLSX.utils.json_to_sheet(financialNotesData);
    XLSX.utils.book_append_sheet(workbook, financialNotesSheet, `Financial Notes ${moment(startDate).format("Y-MMM")}`);

    // Adjustments Sheet
    const adjustmentsData = filteredData.flatMap(item => Object.values(item.dataAdjustment ?? {}).flat(1).flatMap(a => ({
        'Doctor': item.doctorName, ...a,
        Practice: getPracticeName(a.Practice)
    })));
    XLSXaddSheet(workbook, adjustmentsData, `Adjustments ${moment(startDate).format("Y-MMM")}`);

    // WriteOffs Sheet
    const woData = filteredData.flatMap(item => Object.values(item.dataWriteOff ?? {}).flat(1).flatMap(a => ({
        'Doctor': item.doctorName, ...a, WriteOff: a.WriteOff * -1,
        Practice: getPracticeName(a.Practice)
    })));
    XLSXaddSheet(workbook, woData, `Write Offs ${moment(startDate).format("Y-MMM")}`);

    if (singleSheetConfig) {
        const { sheetNameToExtract, newSheetName, downloadFilename } = singleSheetConfig;
        const sheet = workbook.Sheets[sheetNameToExtract];
        if (!sheet) {
            throw new Error(`Sheet "${sheetNameToExtract}" not found in the generated report.`);
        }
        const singleSheetWorkbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(singleSheetWorkbook, sheet, newSheetName);
        XLSX.writeFile(singleSheetWorkbook, downloadFilename);
        return; // End execution here for single sheet download
    }

    // Provider Rates Sheet
    XLSXaddSheet(workbook, providerRates, `Provider Rates ${moment(startDate).format("Y-MMM")}`);

    const modifications = financialNotesData.concat(adjustmentsData).concat(woData).map(item => {
        return {
            Item: item.Title ? 'Financial Note' : item.ADJ_Type ? 'Adjustement' : 'Write Off',
            Practice: item.Practice,
            Provider: item.Provider ?? item.Doctor,
            'Proc Date': item.ProcDate ?? item.Date,
            'Date Entry': item.DateEntry,
            PatNum: item.PatNum,
            Amount: +(item.Amount ?? item.AdjAmt ?? item.WriteOff),
            'B.Care': item['B.Care'] ? +item['B.Care'] : undefined,
            Reason: item.Category ?? item.ADJ_Type ?? item.Writeoff_Note,
            proccode: item.ProcCode
        }
    });
    XLSXaddSheet(workbook, modifications, `Modifications ${moment(startDate).format("Y-MMM")}`);

    // Original logic to download the full report
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Apply yellow highlight to ADP rows that used the provider-level fallback temp cost
    // (cells show all available options so the analyst can identify the correct practice code).
    const _cdpAdp1SheetName = `ADP 1 ${moment(startDate).format("Y-MMM")}`.substring(0, 31).replace(/[\\\/\?\*\[\]\:]/g, '_');
    const _cdpAdp2SheetName = `ADP 2 ${moment(startDate).format("Y-MMM")}`.substring(0, 31).replace(/[\\\/\?\*\[\]\:]/g, '_');
    const _mkCdpAddrs = (indices) => indices.map(i => `${_cdpAdpTcCol}${i + 2}`);
    const _cdpHighlights = [
        ...(_cdpAdp1FallbackIdx.length > 0 ? [{ sheetName: _cdpAdp1SheetName, cellAddrs: _mkCdpAddrs(_cdpAdp1FallbackIdx) }] : []),
        ...(_cdpAdp2FallbackIdx.length > 0 ? [{ sheetName: _cdpAdp2SheetName, cellAddrs: _mkCdpAddrs(_cdpAdp2FallbackIdx) }] : []),
    ];
    const finalCdpBuffer = _cdpHighlights.length > 0 ? applyXlsxHighlights(excelBuffer, _cdpHighlights) : excelBuffer;

    const blob = new Blob([finalCdpBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = singleSheetConfig ? singleSheetConfig.downloadFilename : `${isIndividual ? 'Individual ' : ''}CDP Complete Report ${payDate}.xlsx`;
    link.click();

    // Cleanup
    URL.revokeObjectURL(link.href);
};

export const prepareSageExportData = (data, getDoctorFullName, startDate, isAltPay = false) => {
    // Include providers that have deposit amounts even if procBasedPay is zero
    let filteredData = (data || []).filter(item => (
        item &&
        item?.contractType !== 'UKG HYN' &&
        (Number(item?.procBasedPay || 0) !== 0 || Number(item?.firstDeposit || 0) !== 0 || Number(item?.secondDeposit || 0) !== 0) &&
        item?.contractType !== 'TMPDOC' &&
        item?.contractType !== 'TMPHYN' &&
        !isExcludedProvider(item)
    ));

    const tableData = [];
    let reverseObj = {};

    filteredData.forEach((row) => {
        Object.values(row.data).forEach((practiceData, index) => {
            const data = {
                doctor: getDoctorFullName(row.ABBR),
                practice: (practiceData.practiceInfo[0] && practiceData.practiceInfo[0].practice) || practiceData.practiceDBName,
                payType: row.payType,
                role: row.role,
                payMonth: (row.contractType === 'Greater Of' || row.contractType === 'GOX' || row.contractType === 'GOZ' || row.contractType === 'Sum Of' || row.contractType === 'Collections' || row.contractType === "Greater Of w/HYN" || row.contractType === 'Sum Of w/HYN' || row.contractType === 'Collections w/HYN' || row.contractType === 'GOX w/HYN') ? moment(startDate).format('MMMM YYYY') : '',
                finNote: practiceData.financialNotes || 0,
                // Prefer procBasedPay (CDP canonical source) when present; fall back to practiceTotalPay for legacy data
                debit: (row.contractType === 'Greater Of' || row.contractType === 'GOX' || row.contractType === 'GOZ' || row.contractType === 'Sum Of' || row.contractType === 'Collections' || row.contractType === "Greater Of w/HYN" || row.contractType === 'Sum Of w/HYN' || row.contractType === 'Collections w/HYN' || row.contractType === 'GOX w/HYN') ? (Number(practiceData.procBasedPay || practiceData.practiceTotalPay) || 0) : 0,
                sageAccount: practiceData.sageAccount,
                sageId: (row.sage_ID && row.sage_ID[index]) || ''
            };

            const groupFinNotes = (practiceData.financialNoteList || []).filter(f => f.sageAccount !== data.sageAccount).reduce((acc, cur) => {
                acc[cur.sageAccount] = (acc[cur.sageAccount] || 0) + cur.financialNotes;
                return acc;
            }, {});

            const sageAccountData = Object.entries((groupFinNotes || {})).map((keyPair) => {
                let note = parseFloat(keyPair[1]);
                if (keyPair[1].startsWith("0-")) {
                    const noteString = keyPair[1].split("0-")[1];
                    note = 0 - parseFloat(noteString);
                }

                data.finNote -= note;
                data.debit -= note;
                return { ...data, finNote: note, debit: note, sageAccount: keyPair[0] };
            });

            // Only add entries with non-zero debit amounts
            if (data?.debit !== 0) {
                tableData.push(data);
                if (!reverseObj[data.sageId]) {
                    const dataObj = {
                        [data.sageId]: {}
                    };

                    dataObj[data.sageId] = {
                        practice: data.practice,
                        payMonth: data.payMonth,
                        debit: data.debit,
                        sageAccount: '2230',
                        sageId: data.sageId
                    };

                    Object.assign(reverseObj, dataObj);
                } else {
                    reverseObj[data.sageId]['debit'] = reverseObj[data.sageId]['debit'] + data.debit;
                }
            }

            // Only add sage account entries with non-zero debit amounts
            sageAccountData.forEach(d => {
                if (d?.debit !== 0) {
                    tableData.push(d);
                    if (!reverseObj[d.sageId]) {
                        const dataObj = {
                            [d.sageId]: {}
                        };

                        dataObj[d.sageId] = {
                            practice: d.practice,
                            payMonth: d.payMonth,
                            debit: d.debit,
                            sageAccount: '2230',
                            sageId: d.sageId
                        };

                        Object.assign(reverseObj, dataObj);
                    } else {
                        reverseObj[d.sageId]['debit'] = reverseObj[d.sageId]['debit'] + d.debit;
                    }
                }
            });
        });
    });

    // Only include reverse entries with non-zero debit amounts
    const nonZeroReverseEntries = Object.values(reverseObj).filter(entry => entry?.debit !== 0);
    return tableData.concat(nonZeroReverseEntries);
};

// Helper method to convert Sage data to Excel format
export const convertSageDataToExcelFormat = (sageData, isAltPay = false) => {
    return sageData.map((item, index) => ({
        'PROVIDER': item.doctor,
        'PRACTICE': item.practice,
        'DONOTIMPORT': '',
        'JOURNAL': 'DRPJ',
        'DATE': isAltPay ? moment(exportDates.payDate).format('M/D/YYYY') : moment().endOf('M').format('M/D/YYYY'),
        'REVERSEDATE': '',
        'DESCRIPTION': 'CDP Payroll Journal -ALT',
        'REFERENCE_NO': 'CDP Payroll Journal -ALT',
        'LINE_NO': index + 1,
        'ACCT_NO': item.sageAccount,
        'LOCATION_ID': item.sageId,
        'DEPT_ID': 'COR_DOC_COMP',
        'DOCUMENT': '',
        'MEMO': item.doctor ? `Dr Accrual-${item.doctor}` : `Dr Accrual-${isAltPay ? exportDates.payDate : moment().endOf('M').format('MM/DD/YYYY')}`,
        'DEBIT': Number(item.debit).toFixed(2),
        'CREDIT': '',
        'SOURCEENTITY': 'C_0002',
        'CURRENCY': '',
        'EXCH_RATE_DATE': '',
        'EXCH_RATE_TYPE_ID': '',
        'EXCHANGE_RATE': '',
        'STATE': '',
        'ALLOCATION_ID': '',
        'BILLABLE': '',
        'GLENTRY_CUSTOMERID': '',
        'GLENTRY_EMPLOYEEID': '',
        'GLENTRY_CLASSID': item.role === 'Oral Surgeon' ? 'OS' :
            (item.role === 'Dentist' || item.role === 'Hygienists') ? 'GENERAL' : 'GENERAL'
    }));
};

export const generateFundingDataByFundName = (data, fundName, isAltPay, startDate, isNdp = false, getPracticeName) => {
    // Include providers that have deposit amounts even if procBasedPay is zero
    const filteredData = (data || []).filter(item => (
        item &&
        item?.contractType !== 'UKG HYN' &&
        (Number(item?.procBasedPay || 0) !== 0 || Number(item?.firstDeposit || 0) !== 0 || Number(item?.secondDeposit || 0) !== 0) &&
        item?.contractType !== 'TMPDOC' &&
        item?.contractType !== 'TMPHYN' &&
        !isExcludedProvider(item)
    ));

    let practiceFundsObj = {};

    filteredData.forEach((item) => {
        // For NDP, use orthoLocations instead of data keys
        const practiceSources = isNdp ?
            (item.orthoLocations || []) :
            Object.keys(item.data || {});

        practiceSources.forEach((practiceKey, index) => {
            let practiceData;
            if (isNdp) {
                // Get practice data from item.data using the orthoLocation name
                practiceData = item.data?.[practiceKey] || {};
            } else {
                practiceData = item.data?.[practiceKey] || {};
            }

            if (!practiceData) return;

            let funding = 0;
            if (isNdp) {
                const _ndpNotes = Array.isArray(item.financialNotes) ? item.financialNotes : [];
                const practicePay = practiceData.pay || 0;
                const pInfo = Array.isArray(practiceData.practiceOrthoInfo) ? practiceData.practiceOrthoInfo[0] : {};

                // Provider total and ratio needed only for the rare provider-level (no-practice) notes.
                const ndpPractices = (item.orthoLocations || []).filter(p => item.data && item.data[p]);
                const providerTotalPay = ndpPractices.reduce((sum, p) => sum + (item.data[p]?.pay || 0), 0);
                const practiceRatio = providerTotalPay > 0 ? practicePay / providerTotalPay : 0;

                // Compute this practice's note totals by matching note.practice (set by server per practice).
                // Provider-level notes (no practice field) are distributed proportionally by ratio.
                let practiceTotalNotes = 0, practiceNotesFund = 0;
                _ndpNotes.forEach(note => {
                    const freq = ((note.frequency || '') + '').toLowerCase();
                    const amt = Number(note.financialNotes || 0);
                    const noteHasPractice = note.practice != null || note.practiceId != null;
                    const noteMatchesPractice = note.practice === practiceKey
                        || (pInfo && note.practiceId !== undefined && note.practiceId === pInfo.practiceId);
                    const factor = !noteHasPractice ? practiceRatio : (noteMatchesPractice ? 1 : 0);
                    if (factor === 0) return;

                    practiceTotalNotes += amt * factor;
                    let contrib = 0;
                    if (fundName === 'first') {
                        if (freq === 'first') contrib = amt * factor;
                        else if (freq !== 'second') contrib = (amt / 2) * factor;
                    } else {
                        if (freq === 'second') contrib = amt * factor;
                        else if (freq !== 'first') contrib = (amt / 2) * factor;
                    }
                    practiceNotesFund += contrib;
                });

                // Pure production = pay minus this practice's notes; split 50/50 then add frequency-routed notes.
                const practicePureProduction = practicePay - practiceTotalNotes;
                funding = practicePureProduction / 2 + practiceNotesFund;
            } else {
                const financialNoteListForPractice = practiceData.financialNoteList || [];

                // Compute total of ALL notes that will be redistributed across BOTH funding deposits.
                // Use this (not the precomputed practiceData.financialNotes) as the deduction base so
                // the deduction and the add-back always cancel and total = procBasedPay (= Sage amount).
                const totalValidNotes = financialNoteListForPractice.reduce((sum, fn) => {
                    const freq = (fn.frequency || '').toLowerCase();
                    return (['first', 'second', 'split'].includes(freq))
                        ? sum + (Number(fn.financialNotes) || 0)
                        : sum;
                }, 0);

                let totalFNCosts = 0;
                financialNoteListForPractice.forEach((finNote) => {
                    let fNCost = 0;
                    if ((finNote.frequency || '').toLowerCase() === fundName) {
                        fNCost = Number(finNote.financialNotes);
                    } else if ((finNote.frequency || '').toLowerCase() === 'split') {
                        fNCost = Number(finNote.financialNotes) / 2;
                    }
                    totalFNCosts += fNCost;
                });

                funding = ((practiceData.practiceTotalPay ?? practiceData.procBasedPay) - totalValidNotes) / 2 + totalFNCosts;

                // For alt pay, use full practiceTotalPay
                if (isAltPay) {
                    funding = practiceData.practiceTotalPay ?? practiceData.procBasedPay;
                }
            }

            const practiceInfo = Array.isArray(practiceData.practiceOrthoInfo)
                ? practiceData.practiceOrthoInfo[0]
                : {};

            if (funding !== 0) {
                const locationId = isNdp ?
                    practiceData.sageLocationId || '' :
                    (item.sage_ID?.[index] || '');

                if (!practiceFundsObj[locationId]) {
                    const pracItem = {
                        [locationId]: [{
                            'Batch Name': 'CDP Commissions',
                            'UKG ID': item.ukgid || '',
                            'ADP ID': item.adpId || '',
                            'PROVIDER': item.doctorName,
                            'Cash/Non-cash': 'Cash',
                            'Entity': locationId,
                            'Sage Entity Name': practiceData.sageEntityName || practiceInfo.sageEntityName || '',
                            'Pay Amt': parseFloat(funding || 0).toFixed(2),
                            'UKG Account Name': isNdp ? (item.data.ukgAccountName || '') : item.ukgAccountName || '',
                            'PRACTICE': isNdp ? practiceKey : getPracticeName(practiceKey)
                        }]
                    };
                    Object.assign(practiceFundsObj, pracItem);
                } else {
                    const docPracItem = {
                        'Batch Name': 'CDP Commissions',
                        'UKG ID': item.ukgid || '',
                        'ADP ID': item.adpId || '',
                        'PROVIDER': item.doctorName,
                        'Cash/Non-cash': 'Cash',
                        'Entity': locationId, // For NDP, this is practiceData.sageId
                        'Sage Entity Name': practiceData.sageEntityName || practiceInfo.sageEntityName || '', // Use practiceInfo.sageEntityName for NDP if direct property is missing
                        'Pay Amt': parseFloat(funding || 0).toFixed(2),
                        'UKG Account Name': isNdp ? (item.data.ukgAccountName || '') : item.ukgAccountName || '',
                        'PRACTICE': isNdp ? practiceKey : getPracticeName(practiceKey)
                    };
                    practiceFundsObj[locationId].push(docPracItem);
                }
            }
        });
    });

    // Sort and flatten the results
    const sortedArray = Object.keys(practiceFundsObj).sort().reduce((result, key) => {
        result[key] = practiceFundsObj[key];
        return result;
    }, {});

    let providerLocationArr = [];
    Object.values(sortedArray).forEach((locIdItems) => {
        providerLocationArr = providerLocationArr.concat(locIdItems);
    });

    return providerLocationArr;
};

export const getSageReportCalculatedData = (startDate, data, getDoctorFullName, isNdp = false) => {
    let tableData;

    // Exclude Hygienists from Sage calculations
    data = (data || []).filter(item => !isExcludedProvider(item));

    // Handle NDP data structure
    if (isNdp) {
        let baseRecords = [];
        let reverseRecordsByPractice = {};

        data.forEach((row) => {
            if (!row || !row.data) return;

            // Get financial notes from the row level data
            const rowFinancialNotes = row.financialNotes || [];

            row.orthoLocations.forEach((practiceName) => {
                const practiceData = row.data[practiceName];
                if (!practiceData) return;

                const practiceInfo = Array.isArray(practiceData.practiceOrthoInfo)
                    ? practiceData.practiceOrthoInfo[0]
                    : {};

                // Get practice-specific sageLocationId
                const practiceSageLocationId = practiceData.sageLocationId ||
                    (practiceInfo && practiceInfo.customFields ?
                        (() => {
                            try {
                                const parsedCustomFields = JSON.parse(practiceInfo.customFields);
                                return parsedCustomFields?.sage_ID || '';
                            } catch {
                                return '';
                            }
                        })() : '');

                // Calculate payment components like generateProductionDataX
                const dayRatePay = practiceData.dailyRatePay || 0;
                const productionPay = (practiceData.pay || 0) - dayRatePay - (practiceData.financialNotes || 0);
                const travelStipend = practiceData.travelStipend || 0;
                const finNotesTotal = practiceData.financialNotes || 0;

                // Create separate records for each payment type like generateProductionDataX does
                const records = [];

                // 1. Day Rate payments go to 6000
                if (dayRatePay > 0) {
                    records.push({
                        doctor: row.doctorName || '',
                        practice: practiceName || '',
                        payType: row.payType || row.data?.payType || '',
                        role: 'Orthodontist',
                        payMonth: moment(startDate).format('MMMM YYYY'),
                        finNote: 0,
                        debit: dayRatePay,
                        sageAccount: '6000',
                        sageId: practiceSageLocationId,
                        sageLocationId: practiceSageLocationId
                    });
                }

                // 2. Production payments + travel stipend go to 6040
                const productionAndStipend = productionPay + travelStipend;
                if (productionAndStipend > 0) {
                    records.push({
                        doctor: row.doctorName || '',
                        practice: practiceName || '',
                        payType: row.payType || row.data?.payType || '',
                        role: 'Orthodontist',
                        payMonth: moment(startDate).format('MMMM YYYY'),
                        finNote: 0,
                        debit: productionAndStipend,
                        sageAccount: '6040',
                        sageId: practiceSageLocationId,
                        sageLocationId: practiceSageLocationId
                    });
                }

                // 3. Financial notes - filter by practice and group by sage account
                const practiceFinancialNotes = rowFinancialNotes.filter(note => {
                    // Match by practice name or practiceId
                    return note && (
                        note.practice === practiceName ||
                        (practiceInfo && note.practiceId === practiceInfo.practiceId)
                    );
                });

                const finNotesByAccount = practiceFinancialNotes.reduce((acc, note) => {
                    if (!note || !note.financialNotes) return acc;

                    const account = note?.sageAccount || '6040'; // Default to 6040 for financial notes
                    const amount = Number(note.financialNotes) || 0;
                    if (amount !== 0) {
                        if (!acc[account]) {
                            acc[account] = {
                                amount: 0,
                                notes: []
                            };
                        }
                        acc[account].amount += amount;
                        acc[account].notes.push(note);
                    }
                    return acc;
                }, {});

                // Add financial note records
                Object.entries(finNotesByAccount).forEach(([account, data]) => {
                    if (data.amount !== 0) {
                        // Extract workDate from first financial note (for deposit routing)
                        const firstNote = data.notes && data.notes[0];
                        const noteWorkDate = firstNote?.start || firstNote?.date;

                        records.push({
                            doctor: row.doctorName || '',
                            practice: practiceName || '',
                            payType: row.payType || row.data?.payType || '',
                            role: 'Orthodontist',
                            payMonth: moment(startDate).format('MMMM YYYY'),
                            finNote: data.amount,
                            debit: data.amount,
                            sageAccount: account,
                            sageId: practiceSageLocationId,
                            sageLocationId: practiceSageLocationId,
                            workDate: noteWorkDate,  // ADDED: for deposit routing by date
                            financialNoteList: data.notes
                        });
                    }
                });

                // If there are financial notes total but no individual notes found, create a record
                if (finNotesTotal !== 0 && Object.keys(finNotesByAccount).length === 0) {
                    records.push({
                        doctor: row.doctorName || '',
                        practice: practiceName || '',
                        payType: row.payType || row.data?.payType || '',
                        role: 'Orthodontist',
                        payMonth: moment(startDate).format('MMMM YYYY'),
                        finNote: finNotesTotal,
                        debit: finNotesTotal,
                        sageAccount: '6040', // Default to 6040 if no specific account
                        sageId: practiceSageLocationId,
                        sageLocationId: practiceSageLocationId
                    });
                }

                // Log SAGE data generation for debugging
                if (row.doctorName === 'Adejoke Fatunde' || row.ABBR === 'ADFA') {
                    const practiceTotal = records.reduce((sum, r) => sum + Number(r.debit || 0), 0);
                    records.forEach(r => {
                    });
                }

                // Process all records and create reverse entries
                records.forEach(record => {
                    if (record.debit !== 0) {
                        baseRecords.push({
                            ...record,
                            debit: Number(record.debit || 0).toFixed(2),
                            finNote: Number(record.finNote || 0).toFixed(2)
                        });

                        // Accumulate reverse records by practice
                        if (!reverseRecordsByPractice[record.practice]) {
                            reverseRecordsByPractice[record.practice] = {
                                practice: record.practice,
                                payMonth: record.payMonth,
                                debit: 0,
                                sageAccount: '2230',
                                sageId: record.sageId,
                                sageLocationId: record.sageLocationId
                            };
                        }
                        reverseRecordsByPractice[record.practice].debit += -record.debit;
                    }
                });
            });
        });

        // Convert reverse records object to array and format to 2 decimal places
        const reverseRecords = Object.values(reverseRecordsByPractice).map(record => ({
            ...record,
            debit: Number(record.debit || 0).toFixed(2)
        }));

        tableData = [...baseRecords, ...reverseRecords];

        // GROUP AND SUM DUPLICATE ROWS
        const rowGroups = {};

        tableData.forEach(record => {
            // Create a unique key based on the fields that should be identical
            const key = [
                record.doctor,
                record.practice,
                record.payMonth,
                record.sageAccount,
                record.sageId,
                record.sageLocationId,
                record.role,
                record.payType
            ].join('|');

            if (!rowGroups[key]) {
                rowGroups[key] = {
                    ...record,
                    debit: 0,
                    finNote: 0
                };
            }

            // Sum the debit and finNote values
            rowGroups[key].debit += Number(record.debit) || 0;
            rowGroups[key].finNote += Number(record.finNote) || 0;
        });

        // Convert back to array and format numbers
        tableData = Object.values(rowGroups).map(record => ({
            ...record,
            debit: Number(record.debit || 0).toFixed(2),
            finNote: Number(record.finNote || 0).toFixed(2)
        }));

        // Filter out records with zero debit values
        tableData = tableData.filter(record => {
            const debitStr = String(record.debit);
            return debitStr !== "0.00" && debitStr !== "0" && Number(record.debit) !== 0;
        });
    }

    // Original CDP data handling
    else {
        let baseRecords = [];
        let reverseRecordsByPractice = {};

        data.forEach((row) => {
            if (!row || !row.data) return;

            Object.entries(row.data).forEach(([practiceName, practiceData]) => {
                if (!practiceData) return;

                // practiceTotalPay is the complete pay (base + bonus + notes + TED); procBasedPay is only set for Prod Hurdle
                const debitAmount = Number(practiceData.practiceTotalPay ?? practiceData.procBasedPay ?? 0);
                const baseRecord = {
                    doctor: getDoctorFullName ? getDoctorFullName(row.ABBR) : row.doctorName,
                    practice: practiceData?.practiceInfo?.[0]?.UdaPracticeInfos?.sageEntityName || practiceName,
                    payType: row.payType,
                    role: row.role,
                    payMonth: moment(startDate).format('MMMM YYYY'),
                    finNote: Number(practiceData.financialNotes) || 0,
                    debit: isNaN(debitAmount) ? 0 : debitAmount,
                    sageAccount: practiceData.sageAccount || '6000',
                    sageId: (() => {
                        try {
                            const customFields = practiceData?.practiceInfo?.[0]?.UdaPracticeInfos?.customFields;
                            const parsedCustomFields = customFields ? JSON.parse(customFields) : null;
                            return parsedCustomFields?.sage_ID || row.sage_ID;
                        } catch {
                            return row.sage_ID;
                        }
                    })(),
                    sageLocationId: (() => {
                        try {
                            const customFields = practiceData?.practiceInfo?.[0]?.UdaPracticeInfos?.customFields;
                            const parsedCustomFields = customFields ? JSON.parse(customFields) : null;
                            return parsedCustomFields?.sage_ID || row.sage_ID;
                        } catch {
                            return row.sage_ID;
                        }
                    })()
                };

                // Group financial notes
                const financialNoteList = practiceData.financialNoteList || [];
                const groupFinNotes = financialNoteList.reduce((acc, note) => {
                    if (note?.sageAccount && note?.sageAccount !== baseRecord.sageAccount) {
                        const amount = Number(note.financialNotes) || 0;
                        if (amount !== 0) {
                            acc[note.sageAccount] = (acc[note.sageAccount] || 0) + amount;
                        }
                    }
                    return acc;
                }, {});

                const totalGroupedNotesAmount = Object.values(groupFinNotes).reduce((sum, amount) => sum + amount, 0);
                baseRecord.debit -= totalGroupedNotesAmount;
                baseRecord.finNote -= totalGroupedNotesAmount;

                Object.entries(groupFinNotes).forEach(([account, amount]) => {
                    if (amount === 0) return;

                    const noteRecord = {
                        ...baseRecord,
                        finNote: amount,
                        debit: amount,
                        sageAccount: account
                    };

                    if (noteRecord.debit !== 0) {
                        baseRecords.push({
                            ...noteRecord,
                            debit: Number(noteRecord.debit || 0).toFixed(2),
                            finNote: Number(noteRecord.finNote || 0).toFixed(2)
                        });

                        // Accumulate reverse records by practice
                        if (!reverseRecordsByPractice[noteRecord.practice]) {
                            reverseRecordsByPractice[noteRecord.practice] = {
                                practice: noteRecord.practice,
                                payMonth: noteRecord.payMonth,
                                debit: 0,
                                sageAccount: '2230',
                                sageId: noteRecord.sageId,
                                sageLocationId: noteRecord.sageLocationId
                            };
                        }
                        // Add true inverse: if debit is positive, add negative; if negative, add positive
                        reverseRecordsByPractice[noteRecord.practice].debit += -noteRecord.debit;
                    }
                });

                if (baseRecord.debit !== 0) {
                    baseRecords.push({
                        ...baseRecord,
                        debit: Number(baseRecord.debit || 0).toFixed(2),
                        finNote: Number(baseRecord.finNote || 0).toFixed(2)
                    });

                    // Accumulate reverse records by practice
                    if (!reverseRecordsByPractice[baseRecord.practice]) {
                        reverseRecordsByPractice[baseRecord.practice] = {
                            practice: baseRecord.practice,
                            payMonth: baseRecord.payMonth,
                            debit: 0,
                            sageAccount: '2230',
                            sageId: baseRecord.sageId,
                            sageLocationId: baseRecord.sageLocationId
                        };
                    }
                    // Add true inverse: if debit is positive, add negative; if negative, add positive
                    reverseRecordsByPractice[baseRecord.practice].debit += -baseRecord.debit;
                }
            });
        });

        // Convert reverse records object to array and format to 2 decimal places
        const reverseRecords = Object.values(reverseRecordsByPractice).map(record => ({
            ...record,
            debit: Number(record.debit || 0).toFixed(2)
        }));

        tableData = [...baseRecords, ...reverseRecords];
        // Filter out records with zero debit values
        tableData = tableData.filter(record => {
            const debitStr = String(record.debit);
            return debitStr !== "0.00" && debitStr !== "0" && Number(record.debit) !== 0;
        });
    }

    return tableData;
};

export function getAltPayFundingOrDepositData(data, fundName, startDate, isAltPay) {
    let filteredData = data.filter(item => (
        item?.contractType !== 'UKG HYN' &&
        item?.contractType !== 'TMPDOC' &&
        item?.contractType !== 'TMPHYN' &&
        item?.procBasedPay !== 0
    ));
    let practiceFundsObj = {};
    filteredData.forEach((item) => {
        item = { ...item, ukgCode: item.payType === '1099' ? '1099' : item.ukgCode };
        if (Object.keys(item.data).length === 1) {
            const practiceDB = Object.keys(item.data)[0]
            let funding = (item.data[practiceDB].procBasedPay) / 2;
            if (item.data[practiceDB].financialNotes) {
                const totalPay = (item.data[practiceDB].procBasedPay) / 2;
                const splitFNotes = (item.data[practiceDB].financialNotes) / 2;
                let totalFNCosts = 0;
                if ((item.data[practiceDB].financialNoteList).length > 0) {
                    (item.data[practiceDB].financialNoteList).forEach((finNote) => {
                        let fNCost = 0;
                        if ((finNote.frequency).toLowerCase() === fundName) {
                            fNCost = Number(finNote.financialNotes)
                        }
                        else if ((finNote.frequency).toLowerCase() === 'split') {
                            fNCost = Number(finNote.financialNotes) / 2
                        }
                        totalFNCosts += fNCost;
                    })
                }
                funding = (totalPay - splitFNotes) + totalFNCosts;
            }
            // For alt pay, use the full procBasedPay instead of half
            if (isAltPay) {
                funding = item.data[practiceDB].procBasedPay;
            }
            if (funding !== 0) {
                const locationId = item.sage_ID && item.sage_ID[0];
                if (!practiceFundsObj[locationId]) {
                    practiceFundsObj[locationId] = [];
                }
                // Distribute across UKG codes
                let ukgCodeMap = {};
                let nonCommTotal = 0;
                if (item.data[practiceDB].financialNoteList) {
                    // use practice-level sage account when deciding whether to move note away from COMM
                    const baseSageAccountDB = item.data[practiceDB].sageAccount || '2230';
                    (item.data[practiceDB].financialNoteList).forEach((finNote) => {
                        let ukgCode = item.ukgCode !== '1099' ? finNote.ukg : '1099';
                        if (!ukgCodeMap[ukgCode])
                            ukgCodeMap[ukgCode] = { ukgCode, amount: 0 };
                        let fNCost = 0;
                        if ((finNote.frequency).toLowerCase() === fundName) {
                            fNCost = Number(finNote.financialNotes)
                        }
                        else if ((finNote.frequency).toLowerCase() === 'split') {
                            fNCost = Number(finNote.financialNotes) / 2
                        }
                        if (ukgCode !== 'COMM' && finNote.sageAccount !== baseSageAccountDB) {
                            ukgCodeMap[ukgCode].amount += fNCost;
                            nonCommTotal += fNCost;
                        }
                    });
                }
                // Assign the COMM amount to be the remainder
                if (!ukgCodeMap['COMM']) {
                    ukgCodeMap['COMM'] = { ukgCode: item.ukgCode, amount: 0 };
                }
                ukgCodeMap['COMM'].amount = funding - nonCommTotal;
                // Create entries for each UKG code
                Object.values(ukgCodeMap).forEach(codeData => {
                    if (codeData.amount !== 0) {
                        const entry = {
                            batchName: 'CDP Commissions',
                            practice: item.doctorPracticesArray[0],
                            sageId: locationId,
                            cashType: 'Cash',
                            doctorName: item.doctorName,
                            sageEntityName: item.data[practiceDB].sageEntityName,
                            ukgAccountName: item.ukgAccountName,
                            ukgid: item.ukgid,
                            adpId: item.adpId,
                            ukgCode: codeData.ukgCode,
                            date: startDate,
                            ABBR: item.ABBR,
                            amount: codeData.amount
                        };
                        practiceFundsObj[locationId].push(entry);
                    }
                });
            }
        }
        else if (Object.keys(item.data).length > 1) {
            Object.keys(item.data).forEach((prac, index) => {
                let fundingDeposits = (item.data[prac].procBasedPay) / 2;
                if (item.data[prac].financialNotes) {
                    const totalPay = (item.data[prac].procBasedPay) / 2;
                    const splitFNotes = (item.data[prac].financialNotes) / 2;
                    let totalFNCosts = 0;
                    if ((item.data[prac].financialNoteList).length > 0) {
                        (item.data[prac].financialNoteList).forEach((fNote) => {
                            let fNCost = 0;
                            if ((fNote.frequency).toLowerCase() === fundName) {
                                fNCost = Number(fNote.financialNotes)
                            }
                            else if ((fNote.frequency).toLowerCase() === 'split') {
                                fNCost = Number(fNote.financialNotes) / 2
                            }
                            totalFNCosts += fNCost;
                        })
                    }
                    fundingDeposits = (totalPay - splitFNotes) + totalFNCosts;
                }
                // For alt pay, use the full procBasedPay instead of half
                if (isAltPay) {
                    fundingDeposits = item.data[prac].procBasedPay;
                }
                if (fundingDeposits !== 0) {
                    const sageIdentity = item.sage_ID && item.sage_ID[index];
                    if (!practiceFundsObj[sageIdentity]) {
                        practiceFundsObj[sageIdentity] = [];
                    }
                    // Distribute across UKG codes
                    let ukgCodeMap = {};
                    let nonCommTotal = 0;
                    if (item.data[prac].financialNoteList) {
                        const baseSageAccountPrac = item.data[prac].sageAccount || '2230';
                        (item.data[prac].financialNoteList).forEach((fNote) => {
                            let ukgCode = item.ukgCode !== '1099' ? fNote.ukg : '1099';
                            if (!ukgCodeMap[ukgCode])
                                ukgCodeMap[ukgCode] = { ukgCode, amount: 0 };
                            let fNCost = 0;
                            if ((fNote.frequency).toLowerCase() === fundName) {
                                fNCost = Number(fNote.financialNotes)
                            }
                            else if ((fNote.frequency).toLowerCase() === 'split') {
                                fNCost = Number(fNote.financialNotes) / 2
                            }
                            if (ukgCode !== 'COMM' && fNote.sageAccount !== baseSageAccountPrac) {
                                ukgCodeMap[ukgCode].amount += fNCost;
                                nonCommTotal += fNCost;
                            }
                        });
                    }
                    // Assign the COMM amount to be the remainder
                    if (!ukgCodeMap['COMM']) {
                        ukgCodeMap['COMM'] = { ukgCode: item.ukgCode, amount: 0 };
                    }
                    ukgCodeMap['COMM'].amount = fundingDeposits - nonCommTotal;
                    // Create entries for each UKG code
                    Object.values(ukgCodeMap).forEach(codeData => {
                        if (codeData.amount !== 0) {
                            const entry = {
                                batchName: 'CDP Commissions',
                                practice: item.data[prac].practiceInfo[0].practice,
                                sageEntityName: item.data[prac].sageEntityName,
                                sageId: sageIdentity,
                                cashType: 'Cash',
                                doctorName: item.doctorName,
                                ukgid: item.ukgid,
                                adpId: item.adpId,
                                ABBR: item.ABBR,
                                ukgCode: codeData.ukgCode,
                                ukgAccountName: item.ukgAccountName,
                                date: startDate,
                                amount: codeData.amount
                            };
                            practiceFundsObj[sageIdentity].push(entry);
                        }
                    });
                }
            });
        }
    })
    let providerLocationArr = [];
    Object.values(practiceFundsObj).forEach((locIdItems) => {
        providerLocationArr = providerLocationArr.concat(locIdItems)
    });
    return providerLocationArr;
}

/**
 * UNIFIED SOURCE OF TRUTH: Build a single transaction cache with pre-calculated D1/D2 splits
 * All report sheets derive from this cache to ensure consistency
 *
 * @param {Object} detailedTransactionData - Provider data with transaction lists
 * @returns {Object} transactionCache[provider] = { transactions: [...], totals: {d1, d2} }
 */
function buildUnifiedTransactionCache(detailedTransactionData = {}) {
    const cache = {};

    Object.entries(detailedTransactionData).forEach(([abbr, providerData]) => {
        if (!providerData) return;

        const transactions = [];
        let d1Total = 0, d2Total = 0;

        // Transaction type mappings: key, type, and default SAGE account
        const transactionTypes = [
            { key: 'workedDays', type: 'Worked Day', account: '6000' },
            { key: 'startList', type: 'Start', account: '6040' },
            { key: 'debondList', type: 'Debond', account: '6040' },
            { key: 'sdstartList', type: 'SD-Start', account: '6040' },
            { key: 'sdcontractList', type: 'SD-Contract', account: '6040' },
            { key: 'ph1startList', type: 'PH1-Start', account: '6040' },
            { key: 'dotList', type: 'DOT', account: '6040' },
            { key: 'travelStipendList', type: 'Stipend', account: '6040' }
        ];

        // Step 1: Process all base transactions with 50/50 split (matching earnings report)
        transactionTypes.forEach(({ key, type, account }) => {
            const items = providerData[key] || [];
            if (Array.isArray(items)) {
                items.forEach(item => {
                    const amount = Number(item.rate || item.stipend || 0);
                    const d1Amount = amount / 2;
                    const d2Amount = amount / 2;

                    // Create normalized transaction record
                    const txn = {
                        id: `${abbr}-${key}-${transactions.length}`,
                        provider: providerData.providerName || abbr,
                        abbr,
                        type,
                        practice: item.practice || 'Unknown',
                        date: item.date || '',
                        scheduled: item.scheduled !== false,
                        quantity: item.quantity || 1,
                        baseAmount: amount,
                        // D1/D2 amounts for this transaction
                        d1Amount,
                        d2Amount,
                        // For SAGE: account code (6000 for worked days, 6040 for bonus items)
                        sageAccount: item.sageAccount || account,
                        frequency: null, // Not applicable for base transactions
                        financialNoteData: null
                    };

                    transactions.push(txn);
                    d1Total += d1Amount;
                    d2Total += d2Amount;
                });
            }
        });

        // Step 2: Process financial notes by frequency (matching earnings report method)
        const financialNotes = providerData.financialNotes || [];
        if (Array.isArray(financialNotes)) {
            financialNotes.forEach((note, idx) => {
                const amount = Number(note.financialNotes || 0);
                const freq = (note.frequency || 'split').toLowerCase();

                let d1Amount = 0, d2Amount = 0;
                if (freq === 'split') {
                    d1Amount = amount / 2;
                    d2Amount = amount / 2;
                } else if (freq === 'first') {
                    d1Amount = amount;
                    d2Amount = 0;
                } else if (freq === 'second') {
                    d1Amount = 0;
                    d2Amount = amount;
                } else {
                    // Default to 50/50
                    d1Amount = amount / 2;
                    d2Amount = amount / 2;
                }

                const txn = {
                    id: `${abbr}-financial-${idx}`,
                    provider: providerData.providerName || abbr,
                    abbr,
                    type: 'Financial Note',
                    practice: note.practice || 'Unknown',
                    date: note.start || '',
                    scheduled: false,
                    quantity: 1,
                    baseAmount: amount,
                    d1Amount,
                    d2Amount,
                    sageAccount: note.sageAccount || note.account || '7220', // Financial note account (getDrPay returns 'account', stale data uses 'sageAccount')
                    frequency: freq,
                    financialNoteData: {
                        title: note.title || '',
                        description: note.description || '',
                        ukg: note.ukg || 'COMM'
                    }
                };

                transactions.push(txn);
                d1Total += d1Amount;
                d2Total += d2Amount;
            });
        }

        // Store in cache with totals
        cache[abbr] = {
            providerName: providerData.providerName || abbr,
            transactions,
            totals: {
                d1: d1Total,
                d2: d2Total,
                combined: d1Total + d2Total,
                totalPay: d1Total + d2Total,  // Earnings total (same as combined)
                // Day rate: calculated from worked days if available
                dailyRatePay: providerData.dailyRatePay || 0
            }
        };
    });

    return cache;
}


/**
 * Transform unified cache into ADP detail sheet rows in correct ADP format
 * Provider | Co Code | Batch ID | File # | Earnings 3 Code | Earnings 3 Amount | Adjust DED Code | Adjust DED Amount | Temp Cost Number
 *
 * @param {Object} cache - Unified transaction cache
 * @param {Array} data - Provider array with metadata (adpId, companyCode, orthoLocations, etc.)
 * @param {Date} startDate - Pay period start date
 * @param {String} adpDate1 - D1 batch date (MMDDYYYY)
 * @param {String} adpDate2 - D2 batch date (MMDDYYYY)
 * @param {Map} companyCodeMap - Maps adpId to company code (XS3, TXZ, etc.)
 * @param {Map} tempCostMap - Maps adpId to practice temp cost base
 * @returns {Object} { deposit1Rows: [...], deposit2Rows: [...] }
 */
function transformToADPSheet(cache, data, startDate, adpDate1, adpDate2, companyCodeMap, tempCostMap) {
    const deposit1Rows = [];
    const deposit2Rows = [];

    // Build provider lookup by doctor name
    const providerLookup = new Map();
    (data || []).forEach(provider => {
        if (provider && provider.doctorName) {
            providerLookup.set(provider.doctorName, provider);
        }
    });

    Object.entries(cache).forEach(([abbr, providerCache]) => {
        // Extract provider name from cache transactions (all have same provider name)
        const cachedProviderName = providerCache.providerName || (providerCache.transactions[0]?.provider);
        const provider = providerLookup.get(cachedProviderName);
        if (!provider) {
            return;
        }

        const providerName = provider.doctorName.startsWith('Dr.') ? provider.doctorName : `Dr. ${provider.doctorName}`;
        const coCode = companyCodeMap.get(`adp:${provider.adpId}`) || provider.companyCode || 'XS3';
        const fileNum = provider.adpId || '';

        // Group transactions by practice and type to aggregate amounts
        const practiceTypeGroups = new Map();

        providerCache.transactions.forEach(txn => {
            const practice = txn.practice || 'Unknown';
            const key = `${practice}|${txn.type}`;

            if (!practiceTypeGroups.has(key)) {
                practiceTypeGroups.set(key, {
                    practice,
                    type: txn.type,
                    d1Amount: 0,
                    d2Amount: 0
                });
            }
            const group = practiceTypeGroups.get(key);
            group.d1Amount += txn.d1Amount;
            group.d2Amount += txn.d2Amount;
        });

        // Build practice cost centers
        const practiceCostCenters = new Map();
        (provider.orthoLocations || []).forEach(practiceName => {
            const practiceData = provider.data?.[practiceName];
            if (!practiceData) return;
            const pInfo = Array.isArray(practiceData.practiceOrthoInfo) ? practiceData.practiceOrthoInfo[0] : {};
            try {
                const cf = pInfo?.customFields;
                const parsed = cf ? JSON.parse(cf) : null;
                const costBase = String(parsed?.adpId || parsed?.sage_ID || '');
                if (costBase) {
                    practiceCostCenters.set(practiceName, `${costBase}-DC0000`);
                }
            } catch (e) {}
        });

        // Generate ADP rows for each transaction group
        practiceTypeGroups.forEach(({ practice, type, d1Amount, d2Amount }) => {
            const tempCostNumber = practiceCostCenters.get(practice) || `${tempCostMap.get(fileNum) || 'TX000'}-DC0000`;

            // D1 row
            if (d1Amount > 0.01) {
                deposit1Rows.push({
                    'Provider': providerName,
                    'Co Code': coCode,
                    'Batch ID': adpDate1,
                    'File #': fileNum,
                    'Earnings 3 Code': 'COM',
                    'Earnings 3 Amount': d1Amount.toFixed(2),
                    'Adjust DED Code': '',
                    'Adjust DED Amount': '',
                    'Temp Cost Number': tempCostNumber
                });
            }

            // D2 row
            if (d2Amount > 0.01) {
                deposit2Rows.push({
                    'Provider': providerName,
                    'Co Code': coCode,
                    'Batch ID': adpDate2,
                    'File #': fileNum,
                    'Earnings 3 Code': 'COM',
                    'Earnings 3 Amount': d2Amount.toFixed(2),
                    'Adjust DED Code': '',
                    'Adjust DED Amount': '',
                    'Temp Cost Number': tempCostNumber
                });
            }
        });
    });

    return { deposit1Rows, deposit2Rows };
}

/**
 * Transform unified cache into SAGE GL export rows
 *
 * @param {Object} cache - Unified transaction cache
 * @param {Array} data - Provider data array with sageLocationId, sageAccount, etc.
 * @param {Date} startDate - Start date for month-end formatting
 * @returns {Array} Array of SAGE export rows ready for Excel
 */
function transformToSAGESheet(cache, data, startDate) {
    const positiveRows = [];
    const reverseRows = [];
    const providerLookup = new Map();
    (data || []).forEach(provider => {
        if (provider && provider.doctorName) {
            providerLookup.set(provider.doctorName, provider);
        }
    });

    const endDate = moment(startDate).endOf('M').format('MM/DD/YYYY');
    let lineNo = 1;

    // Collect reverse records by practice for double-entry accounting
    const practiceReverseRecords = new Map();

    Object.entries(cache).forEach(([abbr, providerCache]) => {
        const cachedProviderName = providerCache.providerName || (providerCache.transactions[0]?.provider);
        const provider = providerLookup.get(cachedProviderName);
        if (!provider) {
            return;
        }

        // Group transactions by practice and account to generate GL entries
        const practiceAccountGroups = new Map();
        providerCache.transactions.forEach(txn => {
            const groupKey = `${txn.practice}|${txn.sageAccount}`;
            if (!practiceAccountGroups.has(groupKey)) {
                practiceAccountGroups.set(groupKey, {
                    practice: txn.practice,
                    account: txn.sageAccount,
                    d1Total: 0,
                    d2Total: 0,
                    role: provider.role || 'Orthodontist'
                });
            }
            const group = practiceAccountGroups.get(groupKey);
            group.d1Total += txn.d1Amount;
            group.d2Total += txn.d2Amount;
        });

        // Generate one GL row per practice+account combination
        // Combine D1 and D2 into single debit for the month
        practiceAccountGroups.forEach(({ practice, account, d1Total, d2Total, role }) => {
            const totalDebit = d1Total + d2Total;
            if (Math.abs(totalDebit) > 0.01) {
                // Resolve sageLocationId and sageEntityName for this practice
                let sageLocationId = provider.sageLocationId || '';
                let sageEntityName = '';
                const practiceData = provider.data?.[practice];
                if (practiceData) {
                    const pInfo = Array.isArray(practiceData.practiceOrthoInfo) ? practiceData.practiceOrthoInfo[0] : {};
                    if (practiceData.sageLocationId) {
                        sageLocationId = practiceData.sageLocationId;
                    } else {
                        try {
                            const cf = pInfo?.customFields;
                            const parsed = cf ? JSON.parse(cf) : null;
                            sageLocationId = parsed?.sage_ID || '';
                        } catch (e) {}
                    }
                    // Try direct property first, then fallback to practiceOrthoInfo
                    sageEntityName = practiceData.sageEntityName || pInfo?.sageEntityName || '';
                }

                const drName = cachedProviderName.startsWith('Dr.') ? cachedProviderName : `Dr. ${cachedProviderName}`;
                positiveRows.push({
                    'PROVIDER': drName,
                    'PRACTICE': sageEntityName || practice,
                    'DONOTIMPORT': '',
                    'JOURNAL': 'DRPJ',
                    'DATE': endDate,
                    'REVERSEDATE': '',
                    'DESCRIPTION': 'NDP Payroll Journal -Provider',
                    'REFERENCE_NO': 'NDP Payroll Journal -Provider',
                    'LINE_NO': lineNo++,
                    'ACCT_NO': account,
                    'LOCATION_ID': sageLocationId,
                    'DEPT_ID': 'COR_DOC_COMP',
                    'DOCUMENT': '',
                    'MEMO': `Dr Accrual-${drName}`,
                    'DEBIT': totalDebit.toFixed(2),
                    'CREDIT': '',
                    'SOURCEENTITY': 'C_0002',
                    'CURRENCY': '',
                    'EXCH_RATE_DATE': '',
                    'EXCH_RATE_TYPE_ID': '',
                    'EXCHANGE_RATE': '',
                    'STATE': '',
                    'ALLOCATION_ID': '',
                    'BILLABLE': '',
                    'GLENTRY_CUSTOMERID': '',
                    'GLENTRY_EMPLOYEEID': '',
                    'GLENTRY_CLASSID': role === 'Oral Surgeon' ? 'OS' : 'ORTHO'
                });

                // Accumulate reverse record for this provider+practice combination
                const reverseKey = `${cachedProviderName}|${practice}`;
                if (!practiceReverseRecords.has(reverseKey)) {
                    practiceReverseRecords.set(reverseKey, {
                        provider: cachedProviderName,
                        practice,
                        sageLocationId,
                        sageEntityName,
                        role,
                        reverseDebit: 0
                    });
                }
                practiceReverseRecords.get(reverseKey).reverseDebit -= totalDebit;
            }
        });
    });

    // Add reverse records (account 2230) for double-entry bookkeeping
    practiceReverseRecords.forEach(({ practice, sageLocationId, sageEntityName, role, reverseDebit }) => {
        if (Math.abs(reverseDebit) > 0.01) {
            reverseRows.push({
                'PROVIDER': '',
                'PRACTICE': sageEntityName || practice,
                'DONOTIMPORT': '',
                'JOURNAL': 'DRPJ',
                'DATE': endDate,
                'REVERSEDATE': '',
                'DESCRIPTION': 'NDP Payroll Journal -Provider',
                'REFERENCE_NO': 'NDP Payroll Journal -Provider',
                'LINE_NO': lineNo++,
                'ACCT_NO': '2230',
                'LOCATION_ID': sageLocationId,
                'DEPT_ID': 'COR_DOC_COMP',
                'DOCUMENT': '',
                'MEMO': `Dr Accrual-${moment(startDate).endOf('M').format('MM/DD/YYYY')}`,
                'DEBIT': reverseDebit.toFixed(2),
                'CREDIT': '',
                'SOURCEENTITY': 'C_0002',
                'CURRENCY': '',
                'EXCH_RATE_DATE': '',
                'EXCH_RATE_TYPE_ID': '',
                'EXCHANGE_RATE': '',
                'STATE': '',
                'ALLOCATION_ID': '',
                'BILLABLE': '',
                'GLENTRY_CUSTOMERID': '',
                'GLENTRY_EMPLOYEEID': '',
                'GLENTRY_CLASSID': role === 'Oral Surgeon' ? 'OS' : 'ORTHO'
            });
        }
    });

    // Sort positive rows by practice and account
    positiveRows.sort((a, b) => {
        if (a.PRACTICE !== b.PRACTICE) return (a.PRACTICE || '').localeCompare(b.PRACTICE || '');
        return (a.ACCT_NO || '').localeCompare(b.ACCT_NO || '');
    });

    // Sort reverse rows by practice
    reverseRows.sort((a, b) => {
        return (a.PRACTICE || '').localeCompare(b.PRACTICE || '');
    });

    // Concatenate: positive rows first, then reverse rows
    return [...positiveRows, ...reverseRows];
}

/**
 * Transform unified cache into FUNDING sheet rows (summary by Entity+UKG Account+Code)
 * @param {Object} cache - Unified transaction cache
 * @param {Array} data - Provider data with metadata
 * @param {Date} startDate - Pay period start date
 * @param {Map} coCodeMap - Company code map
 * @returns {Object} { fundingRows1, fundingRows2 }
 */
function transformCacheToFundingRows(cache, data, startDate, coCodeMap = new Map()) {
    const providerLookup = new Map();
    (data || []).forEach(item => {
        if (item && item.doctorName) {
            providerLookup.set(item.doctorName, item);
        }
    });

    const buildFundingForDeposit = (d1Or2) => {
        const fundingMap = new Map();

        Object.entries(cache).forEach(([abbr, providerCache]) => {
            const cachedProviderName = providerCache.providerName;
            const provider = providerLookup.get(cachedProviderName);
            if (!provider) return;

            const ukgId = provider.ukgid || '';
            const adpId = provider.adpId || '';
            const coCode = coCodeMap.get(`adp:${adpId}`) || coCodeMap.get(`ukg:${ukgId}`) || provider.companyCode || 'XS3';
            const payType = provider.data?.payType || provider.payType || 'COMM';

            // Build practice-to-sageEntityName lookup from orthoLocations
            const practiceSageNameMap = new Map();
            (provider.orthoLocations || []).forEach(practiceName => {
                const practiceData = provider.data?.[practiceName];
                let sageEntityName = '';
                if (practiceData) {
                    // Try direct property first, then fallback to practiceOrthoInfo
                    sageEntityName = practiceData.sageEntityName;
                    if (!sageEntityName && Array.isArray(practiceData.practiceOrthoInfo)) {
                        const practiceInfo = practiceData.practiceOrthoInfo[0];
                        sageEntityName = practiceInfo?.sageEntityName || '';
                    }
                }
                practiceSageNameMap.set(practiceName, sageEntityName);
            });

            // Group transactions by practice and type (BONUS/COMM/etc)
            const practiceTypeGroups = new Map();
            providerCache.transactions.forEach(txn => {
                const isD1 = d1Or2 === 'd1';
                const amount = isD1 ? txn.d1Amount : txn.d2Amount;
                if (amount === 0) return;

                const practice = txn.practice || 'Unknown';
                const code = txn.type === 'Financial Note' ? (txn.financialNoteData?.ukg || 'COMM') : 'COMM';
                const key = `${practice}||${code}`;

                if (!practiceTypeGroups.has(key)) {
                    practiceTypeGroups.set(key, { practice, code, amount: 0 });
                }
                const group = practiceTypeGroups.get(key);
                group.amount += amount;
            });

            // Convert to FUNDING format
            practiceTypeGroups.forEach(({ practice, code, amount }) => {
                const mapKey = `${provider.companyCode || 'CDP Commissions'}||${cachedProviderName}||${code}`;
                if (!fundingMap.has(mapKey)) {
                    // Get sageEntityName from practice lookup map, leave blank if not available
                    const sageEntityName = practiceSageNameMap.get(practice) || '';

                    fundingMap.set(mapKey, {
                        'Batch Name': 'CDP Commissions',
                        'UKG ID': ukgId,
                        'ADP ID': adpId,
                        'PROVIDER': cachedProviderName.startsWith('Dr.') ? cachedProviderName : `Dr. ${cachedProviderName}`,
                        'Cash/Non-cash': 'Cash',
                        'Entity': provider.companyCode || 'CDP Commissions',
                        'Sage Entity Name': sageEntityName,
                        'Pay Amt': '0.00',
                        'UKG Account Name': practice,
                        'PRACTICE': practice,
                        '_amount': 0
                    });
                }
                const row = fundingMap.get(mapKey);
                row._amount += amount;
                row['Pay Amt'] = row._amount.toFixed(2);
            });
        });

        return Array.from(fundingMap.values()).map(({ _amount, ...r }) => r);
    };

    return {
        fundingRows1: buildFundingForDeposit('d1'),
        fundingRows2: buildFundingForDeposit('d2')
    };
}

/**
 * Transform unified cache into UKG rows (combined D1+D2 by Provider+Code)
 * @param {Object} cache - Unified transaction cache
 * @param {Array} data - Provider data with metadata
 * @param {Date} startDate - Pay period start date
 * @param {Map} coCodeMap - Company code map
 * @returns {Array} UKG rows with D1/D2 split and combined total
 */
function transformCacheToUkgRows(cache, data, startDate, coCodeMap = new Map()) {
    const providerLookup = new Map();
    (data || []).forEach(item => {
        if (item && item.doctorName) {
            providerLookup.set(item.doctorName, item);
        }
    });

    const ukgMap = new Map();

    Object.entries(cache).forEach(([abbr, providerCache]) => {
        const cachedProviderName = providerCache.providerName;
        const provider = providerLookup.get(cachedProviderName);
        if (!provider) return;

        const ukgId = provider.ukgid || '';
        const adpId = provider.adpId || '';
        const coCode = coCodeMap.get(`adp:${adpId}`) || coCodeMap.get(`ukg:${ukgId}`) || provider.companyCode || 'XS3';
        const payType = provider.data?.payType || provider.payType || 'W2';
        const payMonth = provider.payMonth || moment(startDate).format('MMMM YYYY');
        const drName = cachedProviderName.startsWith('Dr.') ? cachedProviderName : `Dr. ${cachedProviderName}`;

        // Group by code, tracking D1 and D2 amounts separately
        const codeGroups = new Map();
        providerCache.transactions.forEach(txn => {
            const code = txn.type === 'Financial Note' ? (txn.financialNoteData?.ukg || 'COMM') : 'COMM';

            if (!codeGroups.has(code)) {
                codeGroups.set(code, { d1: 0, d2: 0 });
            }
            const group = codeGroups.get(code);
            group.d1 += txn.d1Amount;
            group.d2 += txn.d2Amount;
        });

        // Convert to UKG format (one row per provider+code with D1/D2 split)
        codeGroups.forEach(({ d1, d2 }, code) => {
            const mapKey = `${cachedProviderName}||${code}`;
            const total = d1 + d2;
            if (total > 0) {
                ukgMap.set(mapKey, {
                    'PROVIDER': drName,
                    'ADP ID': adpId,
                    'UKG ID': ukgId,
                    'UKG CODE': code,
                    'PAY TYPE': payType,
                    'SPECIALITY': 'ORTHO',
                    'PAY MONTH': payMonth,
                    '1ST DEPOSIT': d1.toFixed(2),
                    '2ND DEPOSIT': d2.toFixed(2),
                    'AMOUNT': total.toFixed(2)
                });
            }
        });
    });

    return Array.from(ukgMap.values());
}

/**
 * Transform unified cache into DEPOSIT sheet rows
 * Each transaction becomes a row (D1 in DEPOSIT 1, D2 in DEPOSIT 2)
 *
 * @param {Object} cache - Unified transaction cache
 * @param {Array} data - Provider data array with location info
 * @param {Date} startDate - Start date for pay period
 * @returns {Object} { deposit1Rows, deposit2Rows } - Arrays of deposit row objects
 */
function transformCacheToDepositRows(cache, data, startDate) {
    const deposit1Rows = [];
    const deposit2Rows = [];

    // Build provider lookup by doctor name
    const providerLookup = new Map();
    (data || []).forEach(provider => {
        if (provider && provider.doctorName) {
            providerLookup.set(provider.doctorName, provider);
        }
    });

    // Build location resolver
    const resolveLocationId = (provider, practice) => {
        if (!provider) return '';
        const practiceData = provider.data?.[practice];
        if (!practiceData) return provider.sageLocationId || '';
        if (practiceData.sageLocationId) return practiceData.sageLocationId;
        const pInfo = Array.isArray(practiceData.practiceOrthoInfo) ? practiceData.practiceOrthoInfo[0] : {};
        try {
            const cf = pInfo?.customFields;
            return (cf ? JSON.parse(cf)?.sage_ID : null) || provider.sageLocationId || '';
        } catch {
            return provider.sageLocationId || '';
        }
    };

    // Helper: build a deposit row with proper structure
    const buildDepositRowFromCache = (provider, txn, amount, locationId) => ({
        'Company Short Name': '6174247',
        'Employee Id': provider.ukgid || '',
        'ADP ID': provider.adpId || '',
        'Username': getDoctorNameWithPrefix(provider.doctorName) || '',
        'Badge Id': '',
        'SSN#': '',
        'Employee External Id': '',
        'Pay Statement #': '',
        'Check #': '',
        'Pay Period Start Date': '',
        'Pay Period End Date': '',
        'Pay Statement Type': 'REGULAR',
        'Pay Statement Type Key': '',
        'Pay Order': '',
        'Start Date': '',
        'End Date': '',
        'Start Time': '',
        'End Time': '',
        'E/D/T': 'E',
        'E/D/T Code': txn.type === 'Financial Note' ? (txn.financialNoteData?.ukg || 'COMM') : 'COMM',
        'E/D/T Name': '',
        'E/D/T Abbrev': '',
        'E/D/T External ID': '',
        'E Hours': '',
        'E Pieces': '',
        'E Base Rate': '',
        'E Shift': '',
        'E/D/T Amount': Number(amount || 0).toFixed(2),
        'WC Code': '',
        'T EE Gross Wages': '',
        'T ER Gross Wages': '',
        'T EE Gross Subject Wages': '',
        'T ER Gross Subject Wages': '',
        'T EE Subject Wages': '',
        'T ER Subject Wages': '',
        'D/T Employer Amt': '',
        'CC1': locationId,
        'CC2': '',
        'CC3': '',
        'CC4': '',
        'CC5': '',
        'D Vendor': '',
        'Mode': '',
        'Count Zeroes': ''
    });

    // Iterate through cache and create individual transaction rows
    Object.entries(cache).forEach(([abbr, providerCache]) => {
        const cachedProviderName = providerCache.providerName || (providerCache.transactions[0]?.provider);
        const provider = providerLookup.get(cachedProviderName);
        if (!provider) return;

        providerCache.transactions.forEach(txn => {
            const practice = txn.practice || 'Unknown';
            const locationId = resolveLocationId(provider, practice);

            // D1 row (if amount > 0.01)
            if (txn.d1Amount > 0.01) {
                deposit1Rows.push(buildDepositRowFromCache(provider, txn, txn.d1Amount, locationId));
            }

            // D2 row (if amount > 0.01)
            if (txn.d2Amount > 0.01) {
                deposit2Rows.push(buildDepositRowFromCache(provider, txn, txn.d2Amount, locationId));
            }
        });
    });

    return { deposit1Rows, deposit2Rows };
}

/**
 * Build SAGE-based practice totals from unified cache for DEPOSIT sheet generation
 * Returns practice totals split by D1/D2, matching how old SAGE-based approach worked
 *
 * @param {Object} cache - Unified transaction cache
 * @returns {Object} { d1Map, d2Map } - Maps of practice → (doctor → total)
 */
function buildPracticeTotalsFromCache(cache) {
    const d1Map = new Map();
    const d2Map = new Map();

    Object.entries(cache).forEach(([abbr, providerCache]) => {
        const providerName = providerCache.providerName;

        providerCache.transactions.forEach(txn => {
            const practice = txn.practice || 'Unknown';
            const providerPracticeKey = `${providerName}|${practice}`;

            // D1 totals
            if (!d1Map.has(providerPracticeKey)) {
                d1Map.set(providerPracticeKey, 0);
            }
            d1Map.set(providerPracticeKey, d1Map.get(providerPracticeKey) + txn.d1Amount);

            // D2 totals
            if (!d2Map.has(providerPracticeKey)) {
                d2Map.set(providerPracticeKey, 0);
            }
            d2Map.set(providerPracticeKey, d2Map.get(providerPracticeKey) + txn.d2Amount);
        });
    });

    return { d1Map, d2Map };
}

function buildAccountGroupedPracticeTotalsFromCache(cache) {
    const accountGroups = [];
    const seen = new Set();

    Object.entries(cache).forEach(([abbr, providerCache]) => {
        const providerName = providerCache.providerName;

        providerCache.transactions.forEach(txn => {
            const practice = txn.practice || 'Unknown';
            const account = txn.sageAccount || '6000';
            const groupKey = `${providerName}|${practice}|${account}`;

            if (!seen.has(groupKey)) {
                seen.add(groupKey);
                let d1Total = 0;
                let d2Total = 0;

                providerCache.transactions.forEach(t => {
                    if ((t.practice || 'Unknown') === practice && (t.sageAccount || '6000') === account) {
                        d1Total += t.d1Amount;
                        d2Total += t.d2Amount;
                    }
                });

                accountGroups.push({
                    providerName,
                    practice,
                    account,
                    d1Total,
                    d2Total
                });
            }
        });
    });

    return accountGroups;
}

export const exportNdpCompleteReport = async (data, startDate, patientCodes, getPracticeName, sageAccountUkgMap, detailedTransactionData = {}, { previewOnly = false } = {}) => {
    const workbook = XLSX.utils.book_new();
    const payDate = moment(startDate).format('YYYY-MMM');

    // ════════════════════════════════════════════════════════════════
    // QA GATE 1: INPUT DATA STATE VALIDATION
    // ════════════════════════════════════════════════════════════════

    // Detect potential duplicates in input
    const inputDuplicateCheck = new Set();
    const inputDuplicateDetected = [];
    (data || []).forEach((item, idx) => {
        if (item && item.doctorName) {
            const practices = (item.orthoLocations || []).join('|');
            const key = `${item.doctorName}|${practices}`;
            if (inputDuplicateCheck.has(key)) {
                inputDuplicateDetected.push({ idx, key, pay: item.pay });
            }
            inputDuplicateCheck.add(key);
        }
    });

    if (inputDuplicateDetected.length > 0) {
        inputDuplicateDetected.forEach(dup => {
        });
    } else {
    }

    // SURGICAL FIX: Deduplicate input data at source before processing
    // If generateProductionDataX returns duplicate (doctorName, practice) entries,
    // keep ONLY the first occurrence (which has the latest reconciled data)
    const _seenDoctorPractices = new Set();
    const deduplicatedData = (data || []).filter(item => {
        if (!item || !item.doctorName) return true;  // Keep items without doctorName as-is

        const practices = (item.orthoLocations || []).join('|');  // Practices for this doctor
        const key = `${item.doctorName}|${practices}`;

        if (_seenDoctorPractices.has(key)) {
            if (item.doctorName === 'Adejoke Fatunde' || item.ABBR === 'ADFA') {
            }
            return false;  // Skip duplicate
        }
        _seenDoctorPractices.add(key);
        return true;  // Keep first occurrence
    });

    const data_for_export = deduplicatedData;

    // ════════════════════════════════════════════════════════════════
    // QA GATE 2: DEDUPLICATION EFFECTIVENESS
    // ════════════════════════════════════════════════════════════════
    data_for_export.forEach((item, idx) => {
        if (item && item.doctorName === 'Adejoke Fatunde') {
        }
    });

    // ════════════════════════════════════════════════════════════════
    // UNIFIED SOURCE OF TRUTH: Build transaction cache EARLY
    // This must be done before QA-GATE-3 and any sheet generation
    // ════════════════════════════════════════════════════════════════
    const transactionCache = buildUnifiedTransactionCache(detailedTransactionData);

    // Validate cache totals vs expected
    Object.entries(transactionCache).forEach(([abbr, providerCache]) => {
        const { totals } = providerCache;
    });

    // Build practice totals from unified cache (source of truth) for consistent D1/D2 split
    const { d1Map: _sagePracticeD1Map, d2Map: _sagePracticeD2Map } = buildPracticeTotalsFromCache(transactionCache);

    // Build combined totals map from cache
    const _sagePracticeTotalsMap = new Map();
    _sagePracticeD1Map.forEach((d1Total, key) => {
        const d2Total = _sagePracticeD2Map.get(key) || 0;
        _sagePracticeTotalsMap.set(key, d1Total + d2Total);
    });
    _sagePracticeD2Map.forEach((d2Total, key) => {
        if (!_sagePracticeTotalsMap.has(key)) {
            _sagePracticeTotalsMap.set(key, d2Total);
        }
    });

    // Build sageId map from provider data for later use in DEPOSIT rows
    const _sageSidMap = new Map();
    (data_for_export || []).forEach(item => {
        if (!item || !item.doctorName) return;
        (item.orthoLocations || []).forEach(practiceName => {
            const practiceData = item.data?.[practiceName];
            if (!practiceData) return;
            const key = `${item.doctorName}|${practiceName}`;
            let sageId = practiceData.sageLocationId || '';
            if (!sageId) {
                try {
                    const pInfo = Array.isArray(practiceData.practiceOrthoInfo) ? practiceData.practiceOrthoInfo[0] : {};
                    const cf = pInfo?.customFields;
                    sageId = (cf ? JSON.parse(cf)?.sage_ID : null) || '';
                } catch (e) {}
            }
            if (sageId) {
                _sageSidMap.set(key, sageId);
            }
        });
    });

    // NDP Report Sheet - Uses unified transaction cache as source of truth
    const transformCacheToNdpReport = (cache, data) => {
        const providerLookup = new Map();
        (data || []).forEach(item => {
            if (item && item.doctorName) {
                providerLookup.set(item.doctorName, item);
            }
        });

        const ndpRows = [];

        Object.entries(cache).forEach(([abbr, providerCache]) => {
            const cachedProviderName = providerCache.providerName || (providerCache.transactions[0]?.provider);
            const provider = providerLookup.get(cachedProviderName);
            if (!provider) return;

            // Group transactions by practice to build level 2 rows
            const practiceGroups = new Map();
            providerCache.transactions.forEach(txn => {
                const practice = txn.practice || 'Unknown';
                if (!practiceGroups.has(practice)) {
                    practiceGroups.set(practice, {
                        practice,
                        transactions: []
                    });
                }
                practiceGroups.get(practice).transactions.push(txn);
            });

            // Helper to aggregate counts and totals from transactions
            const aggregateMetrics = (transactions) => {
                const metrics = {
                    startCount: 0,
                    startTotal: 0,
                    sdstartCount: 0,
                    sdstartTotal: 0,
                    sdcontractCount: 0,
                    sdcontractTotal: 0,
                    debondCount: 0,
                    debondTotal: 0,
                    ph1startCount: 0,
                    ph1startTotal: 0,
                    dotCount: 0,
                    dotTotal: 0,
                    stipendTotal: 0,
                    financialNotesTotal: 0,
                    d1Total: 0,
                    d2Total: 0
                };

                transactions.forEach(txn => {
                    const quantity = txn.quantity || 1;
                    if (txn.type === 'Start') {
                        metrics.startCount += quantity;
                        metrics.startTotal += txn.d1Amount + txn.d2Amount;
                    } else if (txn.type === 'SD-Start') {
                        metrics.sdstartCount += quantity;
                        metrics.sdstartTotal += txn.d1Amount + txn.d2Amount;
                    } else if (txn.type === 'SD-Contract') {
                        metrics.sdcontractCount += quantity;
                        metrics.sdcontractTotal += txn.d1Amount + txn.d2Amount;
                    } else if (txn.type === 'Debond') {
                        metrics.debondCount += quantity;
                        metrics.debondTotal += txn.d1Amount + txn.d2Amount;
                    } else if (txn.type === 'PH1-Start') {
                        metrics.ph1startCount += quantity;
                        metrics.ph1startTotal += txn.d1Amount + txn.d2Amount;
                    } else if (txn.type === 'DOT') {
                        metrics.dotCount += quantity;
                        metrics.dotTotal += txn.d1Amount + txn.d2Amount;
                    } else if (txn.type === 'Stipend') {
                        metrics.stipendTotal += txn.d1Amount + txn.d2Amount;
                    } else if (txn.type === 'Financial Note') {
                        metrics.financialNotesTotal += txn.d1Amount + txn.d2Amount;
                    }
                    metrics.d1Total += txn.d1Amount;
                    metrics.d2Total += txn.d2Amount;
                });

                return metrics;
            };

            // Build level 1 row (provider summary, TBA practice)
            const level1Metrics = aggregateMetrics(providerCache.transactions);

            // Calculate Day Rate (median of all day rates from all practices)
            let level1DayRate = 0;
            const allDayRates = [];
            (provider.orthoLocations || []).forEach(practice => {
                const practiceData = provider.data?.[practice];
                if (practiceData && practiceData.dateData) {
                    Object.values(practiceData.dateData).forEach(dateItem => {
                        if (dateItem && dateItem.dayRate && Number(dateItem.dayRate) > 0) {
                            allDayRates.push(Number(dateItem.dayRate));
                        }
                    });
                }
            });
            if (allDayRates.length > 0) {
                allDayRates.sort((a, b) => a - b);
                level1DayRate = allDayRates[Math.floor(allDayRates.length / 2)];
            }

            // Calculate Total Pay = First Deposit + Second Deposit
            const level1TotalPay = providerCache.totals.d1 + providerCache.totals.d2;

            let sageEntityLevel1 = '';
            if (provider.data && provider.orthoLocations && provider.orthoLocations.length > 0) {
                const firstPracticeData = provider.data[provider.orthoLocations[0]];
                if (firstPracticeData) {
                    sageEntityLevel1 = firstPracticeData.sageLocationId || '';
                    if (!sageEntityLevel1) {
                        try {
                            const pInfo = Array.isArray(firstPracticeData.practiceOrthoInfo) ? firstPracticeData.practiceOrthoInfo[0] : {};
                            const cf = pInfo?.customFields;
                            sageEntityLevel1 = (cf ? JSON.parse(cf)?.sage_ID : null) || '';
                        } catch (e) {}
                    }
                }
            }

            ndpRows.push({
                level: 1,
                Provider: getDoctorNameWithPrefix(provider.doctorName),
                ABBR: provider.ABBR,
                Practice: 'TBA',
                "Worked Days": provider.worked || 0,
                "Scheduled Days": provider.scheduled || 0,
                "Patient Visits": provider.patientVisits || 0,
                "Exams": provider.exam || 0,
                "Start": level1Metrics.startCount,
                "Start Total": level1Metrics.startTotal,
                "SD-Start": level1Metrics.sdstartCount,
                "SD-Start Total": level1Metrics.sdstartTotal,
                "SD-Contract": level1Metrics.sdcontractCount,
                "SD-Contract Total": level1Metrics.sdcontractTotal,
                "Debond": level1Metrics.debondCount,
                "Debond Total": level1Metrics.debondTotal,
                "PH1-Start": level1Metrics.ph1startCount,
                "PH1-Start Total": level1Metrics.ph1startTotal,
                "DOT": level1Metrics.dotCount,
                "DOT Total": level1Metrics.dotTotal,
                "Stipend Total": level1Metrics.stipendTotal,
                "Day Rate": level1DayRate.toFixed(2),
                "Financial Note": level1Metrics.financialNotesTotal,
                "Total Pay": level1TotalPay.toFixed(2),
                "Dentist Type": "Ortho",
                "First Deposit": providerCache.totals.d1.toFixed(2),
                "Second Deposit": providerCache.totals.d2.toFixed(2),
                "Is Active": provider.isActive ? 'Yes' : 'No',
                "Pay Month": provider.payMonth || '',
                "UKG ID": provider.ukgid || '',
                "ADP ID": provider.adpId || '',
                "Dentist Work Time Type": provider.dentistWorkTimeType || '',
                "Practice ID": '',
                "SAGE Entity": sageEntityLevel1
            });

            // Build level 2 rows (per-practice)
            practiceGroups.forEach(({ practice, transactions }) => {
                const level2Metrics = aggregateMetrics(transactions);

                // Get Practice ID and SAGE Entity
                let practiceId = '';
                let sageEntity = '';
                let level2WorkedDays = 0;
                let level2ScheduledDays = 0;
                let level2PatientVisits = 0;
                let level2Exams = 0;
                let level2DayRate = 0;
                let level2D1Total = 0;
                let level2D2Total = 0;
                if (provider.data && provider.data[practice]) {
                    const practiceData = provider.data[practice];
                    const pInfo = Array.isArray(practiceData.practiceOrthoInfo) ? practiceData.practiceOrthoInfo[0] : {};
                    practiceId = pInfo?.practiceId ? `TX0${pInfo.practiceId}` : '';
                    sageEntity = practiceData.sageLocationId || '';
                    if (!sageEntity) {
                        try {
                            const cf = pInfo?.customFields;
                            sageEntity = (cf ? JSON.parse(cf)?.sage_ID : null) || '';
                        } catch (e) {}
                    }
                    level2WorkedDays = practiceData.worked ? Number(practiceData.worked) : 0;
                    level2ScheduledDays = practiceData.scheduled ? Number(practiceData.scheduled) : 0;
                    level2PatientVisits = practiceData.patientVisits ? Number(practiceData.patientVisits) : 0;
                    level2Exams = practiceData.exam ? Number(practiceData.exam) : 0;

                    // Calculate Day Rate for this practice (median of day rates)
                    const practiceDayRates = [];
                    if (practiceData.dateData) {
                        Object.values(practiceData.dateData).forEach(dateItem => {
                            if (dateItem && dateItem.dayRate && Number(dateItem.dayRate) > 0) {
                                practiceDayRates.push(Number(dateItem.dayRate));
                            }
                        });
                    }
                    if (practiceDayRates.length > 0) {
                        practiceDayRates.sort((a, b) => a - b);
                        level2DayRate = practiceDayRates[Math.floor(practiceDayRates.length / 2)];
                    }
                }

                // Calculate practice-specific deposit totals from transactions
                transactions.forEach(txn => {
                    level2D1Total += txn.d1Amount;
                    level2D2Total += txn.d2Amount;
                });

                // Calculate Total Pay = First Deposit + Second Deposit
                const level2TotalPay = level2D1Total + level2D2Total;

                ndpRows.push({
                    level: 2,
                    Provider: getDoctorNameWithPrefix(provider.doctorName),
                    ABBR: provider.ABBR,
                    Practice: practice,
                    "Worked Days": level2WorkedDays,
                    "Scheduled Days": level2ScheduledDays,
                    "Patient Visits": level2PatientVisits,
                    "Exams": level2Exams,
                    "Start": level2Metrics.startCount,
                    "Start Total": level2Metrics.startTotal,
                    "SD-Start": level2Metrics.sdstartCount,
                    "SD-Start Total": level2Metrics.sdstartTotal,
                    "SD-Contract": level2Metrics.sdcontractCount,
                    "SD-Contract Total": level2Metrics.sdcontractTotal,
                    "Debond": level2Metrics.debondCount,
                    "Debond Total": level2Metrics.debondTotal,
                    "PH1-Start": level2Metrics.ph1startCount,
                    "PH1-Start Total": level2Metrics.ph1startTotal,
                    "DOT": level2Metrics.dotCount,
                    "DOT Total": level2Metrics.dotTotal,
                    "Stipend Total": level2Metrics.stipendTotal,
                    "Day Rate": level2DayRate.toFixed(2),
                    "Financial Note": level2Metrics.financialNotesTotal,
                    "Total Pay": level2TotalPay.toFixed(2),
                    "Dentist Type": '',
                    "First Deposit": level2D1Total.toFixed(2),
                    "Second Deposit": level2D2Total.toFixed(2),
                    "Is Active": '',
                    "Pay Month": '',
                    "UKG ID": '',
                    "ADP ID": provider.adpId || '',
                    "Dentist Work Time Type": '',
                    "Practice ID": practiceId,
                    "SAGE Entity": sageEntity
                });
            });
        });

        return ndpRows;
    };

    const ndpData = transformCacheToNdpReport(transactionCache, data_for_export);

    // Sort NDP data: first by provider name, then by level (1=summary, 2=detail)
    const ndpDataSorted = ndpData.sort((a, b) => {
        const aProvider = (a.Provider || '').trim();
        const bProvider = (b.Provider || '').trim();
        const providerCmp = aProvider.localeCompare(bProvider);
        if (providerCmp !== 0) return providerCmp;
        // Within same provider, level 1 (TBA) comes before level 2 (practices)
        return (a.level || 0) - (b.level || 0);
    });

    const ndpSheet = XLSX.utils.json_to_sheet(ndpDataSorted);
    XLSX.utils.book_append_sheet(workbook, ndpSheet, `NDP Report ${moment(startDate).format("Y-MMM")}`);

    // ════════════════════════════════════════════════════════════════
    // QA GATE 3: SAGE DATA INTEGRITY (from unified cache)
    // ════════════════════════════════════════════════════════════════

    // Verify all Fatunde entries in cache
    const cacheProviders = Object.entries(transactionCache);
    const fatundeInCache = cacheProviders.filter(([abbr, cache]) =>
        cache.providerName && cache.providerName.includes('Adejoke Fatunde')
    );
    fatundeInCache.forEach(([abbr, cache]) => {
    });

    // SAGE Page Sheet - Group by account to show all accounts (e.g., 6000, 6040, 7220)
    const accountGroupedTotals = buildAccountGroupedPracticeTotalsFromCache(transactionCache);
    const providerLookupForSage = new Map();
    (data_for_export || []).forEach(p => {
        if (p.doctorName) providerLookupForSage.set(p.doctorName, p);
    });

    const sagePageSheetData = accountGroupedTotals.flatMap(({ providerName, practice, account, d1Total, d2Total }) => {
        const provider = providerLookupForSage.get(providerName);
        if (!provider) return [];

        let payType = provider?.data?.payType || provider?.payType || 'COMM';
        if (payType?.includes('Smile Leasing')) payType = 'W2';

        const practiceData = provider.data?.[practice];
        if (!practiceData) return [];

        const key = `${providerName}|${practice}`;
        const total = d1Total + d2Total;

        const practiceInfo = Array.isArray(practiceData.practiceOrthoInfo) ? practiceData.practiceOrthoInfo[0] : {};
        const sageEntityName = practiceData.sageEntityName || practiceInfo?.sageEntityName || practice;

        return [{
            PROVIDER: providerName.startsWith('Dr.') ? providerName : `Dr. ${providerName}`,
            PRACTICE: sageEntityName,
            "PAY TYPE": payType,
            SPECIALITY: provider.role || '',
            "Pay Month": provider.payMonth || '',
            "Financial Note": '',
            Debit: total > 0 ? total.toFixed(2) : '',
            "ACCT No.": account,
            "Location ID": _sageSidMap.get(key) || ''
        }];
    }).filter(Boolean);

    // Sort positive rows by provider, then practice, then account
    const positivePageRows = sagePageSheetData.sort((a, b) => {
        if ((a.PROVIDER || '') !== (b.PROVIDER || '')) return (a.PROVIDER || '').localeCompare(b.PROVIDER || '');
        if ((a.PRACTICE || '') !== (b.PRACTICE || '')) return (a.PRACTICE || '').localeCompare(b.PRACTICE || '');
        return (a["ACCT No."] || '').localeCompare(b["ACCT No."] || '');
    });

    // Build reverse records (account 2230) for double-entry bookkeeping by accumulating all accounts per practice
    const practiceReverseMap = new Map();
    positivePageRows.forEach(row => {
        const practiceKey = row.PRACTICE;
        const debitAmount = row.Debit ? Number(row.Debit) : 0;
        if (!practiceReverseMap.has(practiceKey)) {
            practiceReverseMap.set(practiceKey, {
                practice: row.PRACTICE,
                locationId: row["Location ID"],
                reverseDebit: 0
            });
        }
        practiceReverseMap.get(practiceKey).reverseDebit += debitAmount;
    });

    const reversePageRows = [];
    practiceReverseMap.forEach(({ practice, locationId, reverseDebit }) => {
        if (Math.abs(reverseDebit) > 0.01) {
            reversePageRows.push({
                PROVIDER: '',
                PRACTICE: practice,
                "PAY TYPE": '',
                SPECIALITY: '',
                "Pay Month": '',
                "Financial Note": '',
                Debit: (-reverseDebit).toFixed(2),
                "ACCT No.": '2230',
                "Location ID": locationId || ''
            });
        }
    });

    // Sort reverse rows by practice
    reversePageRows.sort((a, b) => {
        return (a.PRACTICE || '').localeCompare(b.PRACTICE || '');
    });

    // Concatenate: positive rows first, then reverse rows
    const sagePageSorted = [...positivePageRows, ...reversePageRows];

    XLSXaddSheet(workbook, sagePageSorted, `SAGE Page ${moment(startDate).format("Y-MMM")}`);

    // Build CO CODE map for UKG enrichment and FUNDING/ADP sheets
    const ndpCompanyCodeMap = new Map();
    (data_for_export || []).forEach(item => {
        if (item) {
            if (item.adpId) ndpCompanyCodeMap.set(`adp:${item.adpId}`, item.companyCode || '');
            if (item.ukgid) ndpCompanyCodeMap.set(`ukg:${item.ukgid}`, item.companyCode || '');
        }
    });

    // Build frequency-routed note amounts per practice (not ratios).
    // Each practice's deposits = (pure production / 2) + routed notes, where notes are routed by frequency alone.
    const _practiceNoteAmountsMap = new Map();  // Key: "doctorName|practiceName", Value: {totalNotes, d1Amount, d2Amount}
    data_for_export.forEach(item => {
        if (!item || !item.orthoLocations) return;
        // CRITICAL FIX: Deduplicate practices array
        const practices = [...new Set(item.orthoLocations || [])];
        const totalPay = practices.reduce((sum, p) => sum + (item.data?.[p]?.pay || 0), 0);

        practices.forEach(practiceName => {
            const practiceData = item.data?.[practiceName];
            if (!practiceData) return;

            const key = `${item.doctorName}|${practiceName}`;
            const pInfo = Array.isArray(practiceData.practiceOrthoInfo) ? practiceData.practiceOrthoInfo[0] : {};
            const _notes = item.financialNotes || [];

            let totalNotes = 0;
            let d1Amount = 0;
            let d2Amount = 0;

            // Calculate frequency-routed note amounts for this practice
            _notes.forEach(note => {
                const freq = ((note.frequency || '') + '').toLowerCase();
                const amt = Number(note.financialNotes || 0);
                const noteHasPractice = note.practice != null || note.practiceId != null;
                const noteMatchesPractice = note.practice === practiceName
                    || (pInfo && note.practiceId !== undefined && note.practiceId === pInfo.practiceId);

                if (!noteHasPractice || noteMatchesPractice) {
                    const factor = noteHasPractice ? 1 : (totalPay > 0 ? practiceData.pay / totalPay : 1 / Math.max(practices.length, 1));
                    const noteAmount = amt * factor;
                    totalNotes += noteAmount;

                    if (freq === 'first') {
                        d1Amount += noteAmount;
                    } else if (freq === 'second') {
                        d2Amount += noteAmount;
                    } else {
                        d1Amount += noteAmount / 2;
                        d2Amount += noteAmount / 2;
                    }
                }
            });

            _practiceNoteAmountsMap.set(key, { totalNotes, d1Amount, d2Amount });
        });
    });
    // Helper: build the fixed deposit row fields, varying only the per-practice/per-code values.
    // tempCostBase (5th param) is the practice-specific ADP cost center code embedded directly in the
    // row so depositRowsToAdpRows never needs to guess it from a potentially-ambiguous map lookup.
    const _buildDepositRow = (item, ukgCode, amount, locationId, tempCostBase = '') => ({
        'Company Short Name': '6174247',
        'Employee Id': item.ukgid || '',
        'ADP ID': item.adpId || '',
        'Username': getDoctorNameWithPrefix(item.doctorName) || '',
        'Badge Id': '',
        'SSN#': '',
        'Employee External Id': '',
        'Pay Statement #': '',
        'Check #': '',
        'Pay Period Start Date': '',
        'Pay Period End Date': '',
        'Pay Statement Type': 'REGULAR',
        'Pay Statement Type Key': '',
        'Pay Order': '',
        'Start Date': '',
        'End Date': '',
        'Start Time': '',
        'End Time': '',
        'E/D/T': 'E',
        'E/D/T Code': ukgCode,
        'E/D/T Name': '',
        'E/D/T Abbrev': '',
        'E/D/T External ID': '',
        'E Hours': '',
        'E Pieces': '',
        'E Base Rate': '',
        'E Shift': '',
        'E/D/T Amount': Number(amount || 0).toFixed(2),
        'WC Code': '',
        'T EE Gross Wages': '',
        'T ER Gross Wages': '',
        'T EE Gross Subject Wages': '',
        'T ER Gross Subject Wages': '',
        'T EE Subject Wages': '',
        'T ER Subject Wages': '',
        'D/T Employer Amt': '',
        'CC1': locationId,
        'CC2': '',
        'CC3': '',
        'CC4': '',
        'CC5': '',
        'D Vendor': '',
        'Mode': '',
        'Count Zeroes': '',
        '_tempCostBase': tempCostBase  // stripped before writing to DEPOSIT sheet; used only for ADP rows
    });

    // DEPOSIT Sheets - Build directly from unified cache (individual transaction rows)
    const { deposit1Rows: cacheDeposit1Rows, deposit2Rows: cacheDeposit2Rows } = transformCacheToDepositRows(transactionCache, data_for_export, startDate);
    const depositData1 = cacheDeposit1Rows;
    const depositData2 = cacheDeposit2Rows;

    // Sort DEPOSIT 1 & 2 by provider name for readability, then strip internal fields
    const depositData1Sorted = depositData1.sort((a, b) =>
        (a.Username || '').localeCompare(b.Username || '')
    );
    const depositSheet1 = XLSX.utils.json_to_sheet(depositData1Sorted.map(({_tempCostBase: _tcb1, ...r}) => r));

    const depositData2Sorted = depositData2.sort((a, b) =>
        (a.Username || '').localeCompare(b.Username || '')
    );
    const depositSheet2 = XLSX.utils.json_to_sheet(depositData2Sorted.map(({_tempCostBase: _tcb2, ...r}) => r));

    // Funding Sheets - Build directly from unified cache (not deposit summaries)
    const { fundingRows1, fundingRows2 } = transformCacheToFundingRows(transactionCache, data_for_export, startDate, ndpCompanyCodeMap);

    // Sort FUNDING 1ST by provider name for readability
    const fundingData1Sorted = fundingRows1.map(item => ({ ...item, PROVIDER: getDoctorNameWithPrefix(item.PROVIDER) })).sort((a, b) =>
        (a.PROVIDER || '').localeCompare(b.PROVIDER || '')
    );
    const fundingSheet1 = XLSX.utils.json_to_sheet(fundingData1Sorted);
    XLSX.utils.book_append_sheet(workbook, fundingSheet1, `FUNDING 1ST ${moment(startDate).format("Y-MMM")}`);

    // Sort FUNDING 2ND by provider name for readability
    const fundingData2Sorted = fundingRows2.map(item => ({ ...item, PROVIDER: getDoctorNameWithPrefix(item.PROVIDER) })).sort((a, b) =>
        (a.PROVIDER || '').localeCompare(b.PROVIDER || '')
    );
    const fundingSheet2 = XLSX.utils.json_to_sheet(fundingData2Sorted);
    XLSX.utils.book_append_sheet(workbook, fundingSheet2, `FUNDING 2ND ${moment(startDate).format("Y-MMM")}`);

    // SAGE Export Sheet - Transform from unified cache
    // NOTE: transformToSAGESheet already returns rows in correct order (positive first, then reverse)
    const sageExportRows = transformToSAGESheet(transactionCache, data_for_export, startDate);
    const sageSheet = XLSX.utils.json_to_sheet(sageExportRows);
    XLSX.utils.book_append_sheet(workbook, sageSheet, `SAGE EXPORT ${moment(startDate).format("Y-MMM")}`);

    // Build deposits first, then derive UKG from them to guarantee exact match with DEPOSIT sheets
    // Helper: resolve the practice-specific sageLocationId the same way SAGE EXPORT does.
    const _resolvePracticeLocationId = (practiceData, fallback) => {
        if (practiceData.sageLocationId) return practiceData.sageLocationId;
        const pInfo = Array.isArray(practiceData.practiceOrthoInfo) ? practiceData.practiceOrthoInfo[0] : {};
        try {
            const cf = pInfo?.customFields;
            return (cf ? JSON.parse(cf)?.sage_ID : null) || fallback || '';
        } catch { return fallback || ''; }
    };

    // ════════════════════════════════════════════════════════════════
    // QA GATE 4: DEPOSIT MAP ROUTING VALIDATION
    // ════════════════════════════════════════════════════════════════

    // Verify all Fatunde entries
    const fatundeMapKeys = Array.from(_sagePracticeTotalsMap.keys()).filter(k => k.includes('Adejoke Fatunde'));
    let totalD1Mapped = 0;
    let totalD2Mapped = 0;

    fatundeMapKeys.forEach(key => {
        const total = _sagePracticeTotalsMap.get(key);
        const d1 = _sagePracticeD1Map.get(key);
        const d2 = _sagePracticeD2Map.get(key);

        // Validate split adds up to total
        const mapSum = d1 + d2;
        if (Math.abs(mapSum - total) > 0.01) {
        } else {
        }

        totalD1Mapped += d1;
        totalD2Mapped += d2;
    });

    // ════════════════════════════════════════════════════════════════
    // QA GATE 5: EXPORT OUTPUT TOTALS VALIDATION (CRITICAL)
    // ════════════════════════════════════════════════════════════════

    // Calculate deposit totals from export data (use sorted data for consistent totals)
    const deposit1Total = depositData1Sorted.reduce((sum, row) => {
        const amount = Number(row['E/D/T Amount'] || 0);
        return sum + amount;
    }, 0);

    const deposit2Total = depositData2Sorted.reduce((sum, row) => {
        const amount = Number(row['E/D/T Amount'] || 0);
        return sum + amount;
    }, 0);

    // Calculate per-practice breakdown for validation
    const d1ByPractice = new Map();
    const d2ByPractice = new Map();

    depositData1.forEach(row => {
        const provider = row['Username'];
        const practice = row['PRACTICE'] || row['Practice'] || 'Unknown';
        const amount = Number(row['E/D/T Amount'] || 0);
        const key = `${provider}|${practice}`;
        d1ByPractice.set(key, (d1ByPractice.get(key) || 0) + amount);
    });

    depositData2.forEach(row => {
        const provider = row['Username'];
        const practice = row['PRACTICE'] || row['Practice'] || 'Unknown';
        const amount = Number(row['E/D/T Amount'] || 0);
        const key = `${provider}|${practice}`;
        d2ByPractice.set(key, (d2ByPractice.get(key) || 0) + amount);
    });

    if (Math.abs(deposit1Total - 12550) < 0.01) {
    } else {
    }

    // Show per-practice breakdown for D1
    d1ByPractice.forEach((amount, key) => {
        if (key.includes('Fatunde')) {
        }
    });

    if (Math.abs(deposit2Total - 11750) < 0.01) {
    } else {
    }

    // Show per-practice breakdown for D2
    d2ByPractice.forEach((amount, key) => {
        if (key.includes('Fatunde')) {
        }
    });

    // Build UKG from cache — uses same transaction data as FUNDING sheets, returns combined D1+D2 rows
    const ndpUkgRows = transformCacheToUkgRows(transactionCache, data_for_export, startDate, ndpCompanyCodeMap);

    const ndpUkgDataSorted = ndpUkgRows.sort((a, b) =>
        (a.PROVIDER || '').localeCompare(b.PROVIDER || '')
    );

    // Add sheets in correct tab order: UKG Page → DEPOSIT 1ST → DEPOSIT 2ND → UKG EXPORT
    const ukgPageSheet = XLSX.utils.json_to_sheet(ndpUkgDataSorted, {
        header: [
                'PROVIDER',
            'ADP ID',
            'UKG ID',
            'UKG CODE',
            'PAY TYPE',
            'SPECIALITY',
            'PAY MONTH',
            '1ST DEPOSIT',
            '2ND DEPOSIT',
            'AMOUNT'
        ]
    });
    XLSX.utils.book_append_sheet(workbook, ukgPageSheet, `UKG Page ${moment(startDate).format("Y-MMM")}`);

    XLSX.utils.book_append_sheet(workbook, depositSheet1, `DEPOSIT 1ST ${moment(startDate).format("Y-MMM")}`);
    XLSX.utils.book_append_sheet(workbook, depositSheet2, `DEPOSIT 2ND ${moment(startDate).format("Y-MMM")}`);

    // UKG Export Sheet
    const ukgSheet = XLSX.utils.json_to_sheet(ndpUkgDataSorted, {
        header: [
                'PROVIDER',
            'ADP ID',
            'UKG ID',
            'UKG CODE',
            'PAY TYPE',
            'SPECIALITY',
            'PAY MONTH',
            '1ST DEPOSIT',
            '2ND DEPOSIT',
            'AMOUNT'
        ]
    });
    XLSX.utils.book_append_sheet(workbook, ukgSheet, `UKG EXPORT ${moment(startDate).format("Y-MMM")}`);

    // ADP 1 and ADP 2 sheets — derived directly from depositData1/depositData2 so amounts are
    // guaranteed identical to the DEPOSIT 1ST / DEPOSIT 2ND sheets.
    const [adpDate1Ndp, adpDate2Ndp] = getAdpPayDates(startDate);

    // Build deterministic provider-level fallback map: adpId → first valid cost base from
    // alphabetically-sorted practices. Prefers customFields.adpId, falls back to customFields.sage_ID
    // when adpId is not configured (sage_ID is the same Location ID the SAGE page shows).
    const ndpTempCostMap = new Map();
    (data || []).forEach(item => {
        if (!item.adpId) return;
        const sortedPracticeNames = [...(item.orthoLocations || [])].sort();
        for (const practiceName of sortedPracticeNames) {
            const practiceData = item.data?.[practiceName];
            if (!practiceData) continue;
            const pInfo = Array.isArray(practiceData.practiceOrthoInfo) ? practiceData.practiceOrthoInfo[0] : {};
            try {
                const cf = pInfo?.customFields;
                const parsed = cf ? JSON.parse(cf) : null;
                const base = String(parsed?.adpId || parsed?.sage_ID || '');
                if (base) {
                    ndpTempCostMap.set(String(item.adpId), base);
                    break;
                }
            } catch (e) {}
        }
    });

    // Build allCostsMap: adpId → sorted array of all temp cost bases across all practices.
    // depositRowsToAdpRows uses this to display all options in cells that fell back to the
    // provider-level lookup (so the analyst can identify which practice code applies).
    // Prefers customFields.adpId; falls back to customFields.sage_ID when adpId is not set.
    const ndpAllCostsMap = new Map();
    (data || []).forEach(item => {
        if (!item.adpId) return;
        const costs = new Set();
        (item.orthoLocations || []).forEach(practiceName => {
            const practiceData = item.data?.[practiceName];
            if (!practiceData) return;
            const pInfo = Array.isArray(practiceData.practiceOrthoInfo) ? practiceData.practiceOrthoInfo[0] : {};
            try {
                const cf = pInfo?.customFields;
                const parsed = cf ? JSON.parse(cf) : null;
                const base = String(parsed?.adpId || parsed?.sage_ID || '');
                if (base) costs.add(base);
            } catch (e) {}
        });
        if (costs.size > 0) ndpAllCostsMap.set(String(item.adpId), [...costs].sort());
    });

    // ════════════════════════════════════════════════════════════════
    // ADP SHEETS: Transform from unified cache (single source of truth)
    // ════════════════════════════════════════════════════════════════
    const { deposit1Rows, deposit2Rows } = transformToADPSheet(
        transactionCache,
        data,
        startDate,
        adpDate1Ndp,
        adpDate2Ndp,
        ndpCompanyCodeMap,
        ndpTempCostMap
    );

    // Sort ADP sheets by provider name for readability
    const deposit1Sorted = deposit1Rows.sort((a, b) =>
        (a.Provider || '').localeCompare(b.Provider || '')
    );
    const deposit2Sorted = deposit2Rows.sort((a, b) =>
        (a.Provider || '').localeCompare(b.Provider || '')
    );

    XLSXaddSheet(workbook, deposit1Sorted, `ADP 1 ${moment(startDate).format("Y-MMM")}`);
    XLSXaddSheet(workbook, deposit2Sorted, `ADP 2 ${moment(startDate).format("Y-MMM")}`);

    // Financial Notes Sheet - With null checks
    const financialNotesData = data.flatMap(item => {
        if (!item) return [];

        const allNotes = [
            ...(item.financialNoteList || []),
            ...(item.data?.financialNotesArr || []),
            ...Object.values(item.data || {}).flatMap(v => v?.financialNoteList || [])
        ].filter(Boolean);

        // Exclude Account 2230 - these only belong in SAGE sheets
        return allNotes.filter(note => (note.sageAccount || '2230') !== '2230').map(note => ({
            'Type': note.notesType || 'NDP Payment',
            'Category': note.category || 'Orthodontic',
            'Title': note.title || 'NDP Compensation',
            'Provider': getDoctorNameWithPrefix(item.doctorName) || '',
            'Practice': note.practice || item.doctorPracticesArray?.[0] || 'N/A',
            'Description': note.desc || 'NDP provider compensation',
            'Frequency': note.frequency || 'Monthly',
            'Taxable': note.isTaxable ? 'Yes' : 'No',
            'Account': note.sageAccount || '2230',
            'UKG Code': sageAccountUkgMap[note.sageAccount] || '',
            'Amount': Number(note.financialNotes || 0).toFixed(2),
            'B.Care': note.bcare || '',
            'Date': note.dateString || moment(startDate).format('YYYY-MM-DD'),
            'Published': 'Yes'
        }));
    });

    const financialNotesSheet = XLSX.utils.json_to_sheet(financialNotesData);
    XLSX.utils.book_append_sheet(workbook, financialNotesSheet, `Financial Notes ${moment(startDate).format("Y-MMM")}`);

    // Patient Codes Sheet
    const patientCodesData = (patientCodes || []).map(item => ({ //The practice column will now use the getPracticeOrthoLocation to get the correct practice name
        'Provider': item.provider,
        'Date': moment(item.date).format('YYYY-MM-DD'),
        'PatientID': item.patientId,
        'Practice': getPracticeName(item.practice),
        'Procedure Code': item.procedureCode,
        'Code Group': item.codeGroup,
        'Action': item.action,
        'History': item.history,
        'Patient Full Name': item.PatientFullName,
        'Patient Birthdate': item.PatientBirthdate ? moment(item.PatientBirthdate).format('YYYY-MM-DD') : '',
        'Gender': item.Gender,
        'State': item.state,
    }));

    const patientCodesSheet = XLSX.utils.json_to_sheet(patientCodesData);
    XLSX.utils.book_append_sheet(workbook, patientCodesSheet, `Patient Codes`);

    // Provider Profiles Sheet - one summary row per provider (using unified cache data)
    // Build provider name → cache mapping for efficient lookup
    const providerNameToCacheMap = new Map();
    Object.entries(transactionCache).forEach(([abbr, cacheData]) => {
        if (cacheData && cacheData.providerName) {
            providerNameToCacheMap.set(cacheData.providerName, cacheData);
        }
    });

    const providerProfilesData = data_for_export
        .filter(item => item && item.ABBR && item.doctorName)
        .map(item => {
            const practices = Array.from(new Set(item.orthoLocations || [])).filter(p => item.data && item.data[p]);

            // Worked days: sum from all practices, fallback to item.worked if available
            let workedDays = item.worked || 0;
            if (workedDays === 0) {
                workedDays = practices.reduce((sum, p) => {
                    const pData = item.data[p];
                    return sum + ((pData && pData.worked) ? Number(pData.worked) : 0);
                }, 0);
            }

            // Day rate: read from individual scheduled day transactions (same source as ADP sheets)
            // Each dateItem in dateData has dayRate field for scheduled work
            let dayRate = 0;
            const scheduledDayRates = [];
            for (const practiceName of practices) {
                const pData = item.data[practiceName];
                if (pData && pData.dateData && typeof pData.dateData === 'object') {
                    Object.values(pData.dateData).forEach(dateItem => {
                        // Collect dayRate from transactions where pay > 0 (indicates scheduled work)
                        if (dateItem && dateItem.dayRate && Number(dateItem.dayRate) > 0) {
                            scheduledDayRates.push(Number(dateItem.dayRate));
                        }
                    });
                }
            }
            if (scheduledDayRates.length > 0) {
                // Use the median/most common rate (all should be same, but use median for safety)
                scheduledDayRates.sort((a, b) => a - b);
                dayRate = scheduledDayRates[Math.floor(scheduledDayRates.length / 2)];
            }

            // Total Pay = (Day Rate × Worked Days) + bonus totals + stipend + financial notes
            const baseEarnings = Number(dayRate || 0) * Number(workedDays || 0);
            const bonusEarnings = Number(item.startTotal || 0) + Number(item.sdstartTotal || 0)
                + Number(item.sdcontractTotal || 0) + Number(item.debondTotal || 0)
                + Number(item.ph1startTotal || 0) + Number(item.dotTotal || 0);
            const earnings = baseEarnings + bonusEarnings + Number(item.stipendTotal || 0) + Number(item.financialNotesTotal || 0);

            const startCount = typeof item.start === 'object' && item.start !== null
                ? Object.values(item.start).reduce((s, v) => s + (Array.isArray(v) ? v.length : (Number(v) || 0)), 0)
                : (Number(item.start) || 0);
            const sdStartCount = typeof item.sdstart === 'object' && item.sdstart !== null
                ? Object.values(item.sdstart).reduce((s, v) => s + (Array.isArray(v) ? v.length : (Number(v) || 0)), 0)
                : (Number(item.sdstart) || 0);
            const sdContractCount = typeof item.sdcontract === 'object' && item.sdcontract !== null
                ? Object.values(item.sdcontract).reduce((s, v) => s + (Array.isArray(v) ? v.length : (Number(v) || 0)), 0)
                : (Number(item.sdcontract) || 0);
            const debondCount = typeof item.debond === 'object' && item.debond !== null
                ? Object.values(item.debond).reduce((s, v) => s + (Array.isArray(v) ? v.length : (Number(v) || 0)), 0)
                : (Number(item.debond) || 0);
            const ph1StartCount = typeof item.ph1start === 'object' && item.ph1start !== null
                ? Object.values(item.ph1start).reduce((s, v) => s + (Array.isArray(v) ? v.length : (Number(v) || 0)), 0)
                : (Number(item.ph1start) || 0);
            const dotCount = typeof item.dot === 'object' && item.dot !== null
                ? Object.values(item.dot).reduce((s, v) => s + (Array.isArray(v) ? v.length : (Number(v) || 0)), 0)
                : (Number(item.dot) || 0);

            return {
                'Provider': getDoctorNameWithPrefix(item.doctorName),
                'ABBR': item.ABBR,
                'Day Rate': Number(dayRate).toFixed(2),
                'Starts': startCount,
                'Start Total ($)': item.startTotal || 0,
                'SD-Starts': sdStartCount,
                'SD-Start Total ($)': item.sdstartTotal || 0,
                'SD-Contracts': sdContractCount,
                'SD-Contract Total ($)': item.sdcontractTotal || 0,
                'Debonds': debondCount,
                'Debond Total ($)': item.debondTotal || 0,
                'PH1 Starts': ph1StartCount,
                'PH1 Start Total ($)': item.ph1startTotal || 0,
                'DOT': dotCount,
                'DOT Total ($)': item.dotTotal || 0,
                'Total PTs': item.patientVisits || 0,
                'Exams': item.exam || 0,
                'Worked Days': workedDays,
                'Scheduled Days': item.scheduled || 0,
                'Stipend Total ($)': item.stipendTotal || 0,
                'Financial Note ($)': item.financialNotesTotal || 0,
                'Total Pay ($)': earnings ? Number(earnings).toFixed(2) : 0,
                'Is Active': item.isActive ? 'Yes' : 'No',
                'Pay Month': item.payMonth || moment(startDate).format('MMMM YYYY'),
                'UKG ID': item.ukgid || '',
                'ADP ID': item.adpId || '',
            };
        });

    const providerProfilesSheet = XLSX.utils.json_to_sheet(providerProfilesData);
    XLSX.utils.book_append_sheet(workbook, providerProfilesSheet, `Provider Profiles ${moment(startDate).format("Y-MMM")}`);

    // Build validation sheet using unified data from all other sheets
    const validationData = buildNdpValidationData({
        ndpRows: ndpDataSorted,
        sagePageRows: sagePageSorted,
        sageExportRows: sageExportRows,
        funding1Rows: fundingData1Sorted,
        funding2Rows: fundingData2Sorted,
        ukgRows: ndpUkgDataSorted,
        deposit1Rows: depositData1Sorted,
        deposit2Rows: depositData2Sorted,
        adp1Rows: deposit1Sorted,
        adp2Rows: deposit2Sorted,
        financialNotesRows: financialNotesData
    });

    // Append validation sheet to workbook
    const validationSheet = XLSX.utils.json_to_sheet(validationData.excelRows);
    XLSX.utils.book_append_sheet(workbook, validationSheet, `Validation ${moment(startDate).format("Y-MMM")}`);

    // If previewOnly mode, return workbook and validation data without downloading
    if (previewOnly) {
        return { workbook, validationData };
    }

    // Generate and download the file
    const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
    });

    // Apply yellow highlight to ADP rows that used the provider-level fallback temp cost
    // (cells show all available options so the analyst can identify the correct practice code).
    const _ndpAdp1SheetName = `ADP 1 ${moment(startDate).format("Y-MMM")}`.substring(0, 31).replace(/[\\\/\?\*\[\]\:]/g, '_');
    const _ndpAdp2SheetName = `ADP 2 ${moment(startDate).format("Y-MMM")}`.substring(0, 31).replace(/[\\\/\?\*\[\]\:]/g, '_');
    const _ndpAdp1FallbackIdx = []; // Unified cache pre-calculates all splits, no fallbacks needed
    const _ndpAdp2FallbackIdx = []; // Unified cache pre-calculates all splits, no fallbacks needed
    const _mkNdpAddrs = (indices) => indices.map(i => `${i + 2}`);

    // Build highlights: ADP fallback rows + validation sheet green/red cells
    const validationSheetName = `Validation ${moment(startDate).format("Y-MMM")}`;
    const _ndpHighlights = [
        ...(_ndpAdp1FallbackIdx.length > 0 ? [{ sheetName: _ndpAdp1SheetName, cellAddrs: _mkNdpAddrs(_ndpAdp1FallbackIdx) }] : []),
        ...(_ndpAdp2FallbackIdx.length > 0 ? [{ sheetName: _ndpAdp2SheetName, cellAddrs: _mkNdpAddrs(_ndpAdp2FallbackIdx) }] : []),
        ...(validationData.cellHighlights.green.length > 0 ? [{ sheetName: validationSheetName, cellAddrs: validationData.cellHighlights.green, color: 'green' }] : []),
        ...(validationData.cellHighlights.red.length > 0 ? [{ sheetName: validationSheetName, cellAddrs: validationData.cellHighlights.red, color: 'red' }] : [])
    ];
    const finalNdpBuffer = _ndpHighlights.length > 0 ? applyXlsxHighlights(excelBuffer, _ndpHighlights) : excelBuffer;

    const blob = new Blob([finalNdpBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `NDP Complete Report ${payDate}.xlsx`;
    link.click();

    // Cleanup
    URL.revokeObjectURL(link.href);

    // ════════════════════════════════════════════════════════════════
    // FINAL QUALITY ASSURANCE SUMMARY
    // ════════════════════════════════════════════════════════════════


    const criteria1Pass = Math.abs(deposit1Total - 12550) < 0.01;
    const criteria2Pass = Math.abs(deposit2Total - 11750) < 0.01;

    if (criteria1Pass && criteria2Pass) {
    } else {
    }



};

export const reconcileProviderMetrics = (providerObj, drPay) => {
  if (!providerObj || !drPay) {
    return providerObj;
  }

  const getMetricValue = (fresh, stale) =>
    fresh !== undefined && fresh !== null ? fresh : stale;

  // Direct matches (same name in both sources)
  if (drPay.worked !== undefined) {
    providerObj.worked = getMetricValue(drPay.worked, providerObj.worked);
  }
  if (drPay.scheduled !== undefined) {
    providerObj.scheduled = getMetricValue(drPay.scheduled, providerObj.scheduled);
  }
  if (drPay.start !== undefined) {
    providerObj.start = getMetricValue(drPay.start, providerObj.start);
  }
  if (drPay.startTotal !== undefined) {
    providerObj.startTotal = getMetricValue(drPay.startTotal, providerObj.startTotal);
  }
  if (drPay.sdstart !== undefined) {
    providerObj.sdstart = getMetricValue(drPay.sdstart, providerObj.sdstart);
  }
  if (drPay.sdstartTotal !== undefined) {
    providerObj.sdstartTotal = getMetricValue(drPay.sdstartTotal, providerObj.sdstartTotal);
  }
  if (drPay.sdcontract !== undefined) {
    providerObj.sdcontract = getMetricValue(drPay.sdcontract, providerObj.sdcontract);
  }
  if (drPay.sdcontractTotal !== undefined) {
    providerObj.sdcontractTotal = getMetricValue(drPay.sdcontractTotal, providerObj.sdcontractTotal);
  }
  if (drPay.debond !== undefined) {
    providerObj.debond = getMetricValue(drPay.debond, providerObj.debond);
  }
  if (drPay.debondTotal !== undefined) {
    providerObj.debondTotal = getMetricValue(drPay.debondTotal, providerObj.debondTotal);
  }
  if (drPay.ph1start !== undefined) {
    providerObj.ph1start = getMetricValue(drPay.ph1start, providerObj.ph1start);
  }
  if (drPay.ph1startTotal !== undefined) {
    providerObj.ph1startTotal = getMetricValue(drPay.ph1startTotal, providerObj.ph1startTotal);
  }
  if (drPay.dot !== undefined) {
    providerObj.dot = getMetricValue(drPay.dot, providerObj.dot);
  }
  if (drPay.dotTotal !== undefined) {
    providerObj.dotTotal = getMetricValue(drPay.dotTotal, providerObj.dotTotal);
  }
  if (drPay.patientVisits !== undefined) {
    providerObj.patientVisits = getMetricValue(drPay.patientVisits, providerObj.patientVisits);
  }

  // Field name mismatches (map to target field name)
  if (drPay.exams !== undefined) {
    providerObj.exam = getMetricValue(drPay.exams, providerObj.exam);
  }
  if (drPay.stipend !== undefined) {
    providerObj.travelStipend = getMetricValue(drPay.stipend, providerObj.travelStipend);
  }
  if (drPay.dayRateTotal !== undefined) {
    providerObj.dailyRatePay = getMetricValue(drPay.dayRateTotal, providerObj.dailyRatePay);
  }
  if (drPay.totalFirstDeposit !== undefined) {
    providerObj.firstDeposit = getMetricValue(drPay.totalFirstDeposit, providerObj.firstDeposit);
  }
  if (drPay.totalSecondDeposit !== undefined) {
    providerObj.secondDeposit = getMetricValue(drPay.totalSecondDeposit, providerObj.secondDeposit);
  }
  // CRITICAL: Preserve row.financialNotes array (practice-level details needed by export)
  // Only update practice-level numeric totals, not the detail array
  if (drPay.financialNotesTotal !== undefined && !Array.isArray(providerObj.financialNotes)) {
    providerObj.financialNotes = getMetricValue(drPay.financialNotesTotal, providerObj.financialNotes);
  }
  if (drPay.pay !== undefined) {
    providerObj.pay = getMetricValue(drPay.pay, providerObj.pay);
  }

  return providerObj;
};

export const reconcilePracticeMetrics = (providerObj, practiceMetricsMap) => {
  if (!providerObj || !providerObj.data || !practiceMetricsMap) {
    return;
  }

  const getMetricValue = (fresh, stale) =>
    fresh !== undefined && fresh !== null ? fresh : stale;

  Object.keys(practiceMetricsMap).forEach((practiceName) => {
    const freshMetrics = practiceMetricsMap[practiceName];
    if (!freshMetrics) return;

    const practiceData = providerObj.data[practiceName];
    if (!practiceData) return;

    // Update practice-level numeric metrics with fresh data
    if (freshMetrics.worked !== undefined) {
      practiceData.worked = getMetricValue(freshMetrics.worked, practiceData.worked);
    }
    if (freshMetrics.scheduled !== undefined) {
      practiceData.scheduled = getMetricValue(freshMetrics.scheduled, practiceData.scheduled);
    }
    if (freshMetrics.dayRateTotal !== undefined) {
      practiceData.dailyRatePay = getMetricValue(freshMetrics.dayRateTotal, practiceData.dailyRatePay);
    }
    if (freshMetrics.startTotal !== undefined) {
      practiceData.startTotal = getMetricValue(freshMetrics.startTotal, practiceData.startTotal);
    }
    if (freshMetrics.sdstartTotal !== undefined) {
      practiceData.sdstartTotal = getMetricValue(freshMetrics.sdstartTotal, practiceData.sdstartTotal);
    }
    if (freshMetrics.sdcontractTotal !== undefined) {
      practiceData.sdcontractTotal = getMetricValue(freshMetrics.sdcontractTotal, practiceData.sdcontractTotal);
    }
    if (freshMetrics.debondTotal !== undefined) {
      practiceData.debondTotal = getMetricValue(freshMetrics.debondTotal, practiceData.debondTotal);
    }
    if (freshMetrics.ph1startTotal !== undefined) {
      practiceData.ph1startTotal = getMetricValue(freshMetrics.ph1startTotal, practiceData.ph1startTotal);
    }
    if (freshMetrics.dotTotal !== undefined) {
      practiceData.dotTotal = getMetricValue(freshMetrics.dotTotal, practiceData.dotTotal);
    }
    if (freshMetrics.stipend !== undefined) {
      practiceData.travelStipend = getMetricValue(freshMetrics.stipend, practiceData.travelStipend);
    }
    if (freshMetrics.exams !== undefined) {
      practiceData.exam = getMetricValue(freshMetrics.exams, practiceData.exam);
    }
    if (freshMetrics.patientVisits !== undefined) {
      practiceData.patientVisits = getMetricValue(freshMetrics.patientVisits, practiceData.patientVisits);
    }
    if (freshMetrics.financialNotesTotal !== undefined) {
      practiceData.financialNotes = getMetricValue(freshMetrics.financialNotesTotal, practiceData.financialNotes);
    }
    // Field name mappings
    if (freshMetrics.start !== undefined) {
      practiceData.start = getMetricValue(freshMetrics.start, practiceData.start);
    }
    if (freshMetrics.sdstart !== undefined) {
      practiceData.sdstart = getMetricValue(freshMetrics.sdstart, practiceData.sdstart);
    }
    if (freshMetrics.sdcontract !== undefined) {
      practiceData.sdcontract = getMetricValue(freshMetrics.sdcontract, practiceData.sdcontract);
    }
    if (freshMetrics.debond !== undefined) {
      practiceData.debond = getMetricValue(freshMetrics.debond, practiceData.debond);
    }
    if (freshMetrics.ph1start !== undefined) {
      practiceData.ph1start = getMetricValue(freshMetrics.ph1start, practiceData.ph1start);
    }
    if (freshMetrics.dot !== undefined) {
      practiceData.dot = getMetricValue(freshMetrics.dot, practiceData.dot);
    }
    // The critical field: provider-level pay should be recalculated, but practice-level is used by export
    if (freshMetrics.pay !== undefined) {
      practiceData.pay = getMetricValue(freshMetrics.pay, practiceData.pay);
    }
  });
};

export const exportProviderRates = ({ providerRates, reportData, practiceListArray }) => {
    const variables = Object.values(providerRates.configs);
    const practiceMap = practiceListArray.reduce((acc, practice) => {
        acc[practice.id] = practice.practice;
        return acc;
    }, {});
    const doctorMap = reportData.reduce((acc, item) => {
        acc[item.doctorId] = item;
        return acc;
    }, {});

    const data = Object.values(doctorMap).flatMap(item => {
        return providerRates[item.doctorId]?.map(r => {
            const rate = r.value ?? {};
            const obj = variables.reduce((acc, variable) => {
                acc[variable.label] = typeof rate[variable.name] === 'boolean' ? (rate[variable.name] ? 'Yes' : 'No') : rate[variable.name] ?? '';
                return acc;
            }, {});
            return {
                'Provider': item.doctorName,
                'Practice': practiceMap[r.practiceId] ?? 'ALL',
                'Dates': `${r.range[0]} to ${r.range[1] ?? 'FUTURE'}`,
                ...obj
            }
        }) ?? [];
    })
    return data;
};
